<template>
    <div id="appraisals-appraise">
        <PhotoWidget ref="PhotoWidget"></PhotoWidget>
        <GenericPopups ref="GenericPopups"></GenericPopups>
        <LoadingSpinner :busy="loading"></LoadingSpinner>
        <WeightValidation ref="WeightValidation"></WeightValidation>
        <b-card v-if="order && !loading" :header-bg-variant="_orderCardHeaderBGColor">
            <template v-slot:header>
                <OrderCardHeader :order="order"></OrderCardHeader>
            </template>

            <template v-if="this.$parent.state === 'appraisals-appraise' && activeItem">
                <!--**************************
                           START INVENTORY
                   ***************************-->
                <b-row>

                    <b-col>
                        <video-streamer-control camera="scaleCamera" v-on:photo-taken="savePhotoAsync($event)" mode="picture"
                                                ref="picturePreview"></video-streamer-control>
                        <div class="d-flex my-2 align-items-center">
                            <b-button variant="success" class="mr-3 font-weight-bold"
                                      :disabled="!scaleStable || !_canEdit"
                                      @click="captureWeightAndPhoto" v-if="!scaleSaved">
                                <i class="fa-regular fa-image"></i> + <i class="fa-regular fa-balance-scale"></i> Capture
                            </b-button>
                            <b-button variant="secondary" class="mr-3" :disabled="!scaleStable || !_canEdit"
                                      @click="recaptureWeightAndPhoto" v-else>
                                <i class="fa-regular fa-image"></i> + <i class="fa-regular fa-balance-scale"></i> Re-Capture
                            </b-button>

                            <b-button variant="info" class="text-white mr-3" @click="takePhoto"
                                      :disabled="!scaleSaved || !_canEdit"><i
                                class="fa-regular fa-image"> +</i></b-button>
                            <ScaleWeight v-on:scale-stable="scaleStable = $event"
                                         v-on:scale-weight="scaleWeight = $event"
                                         sticky-connection ref="scale"></ScaleWeight>
                        </div>
                        <b-form-row class="my-3">
                            <b-col cols="3" class="mb-2" v-for="(item, index) in itemDescription.pictures" :key="index">
                                <img style="width: 100%;" :src="createImageURL(item)" alt=""
                                     v-on:click="activateImageModal(item)">
                                <b-button size="sm" variant="link" pill class="delete-photo-icon"
                                          @click="removePhoto(index)">
                                    <i class="fa-solid fa-times-circle text-danger"></i>
                                </b-button>
                            </b-col>
                        </b-form-row>

                        <b-card header-class="font-weight-bold" body-class="p-0">
                            <template #header class="d-inline">
                                Item Summary
                                <div v-if="isCoin(itemDescription.itemComponents[0])" class="float-right">
                                    <small>{{ coinWeightManual ? "Manual Mode" : "Automatic Mode" }}</small>
                                    <b-form-checkbox
                                            id="appraiser-certificate"
                                            v-model="_manualMode"
                                            name="badwolf"
                                            v-if="isCoin(itemDescription.itemComponents[0])"
                                            class="mt-0 float-right ml-2"
                                            switch>
                                    </b-form-checkbox>
                                </div>
                            </template>
                            <b-table-simple outlined bordered s class="m-0">
                                <b-thead>
                                    <b-tr>
                                        <b-th>Element</b-th>
                                        <b-th>Weight (dwt)</b-th>
                                        <b-th>EAV</b-th>
                                    </b-tr>
                                </b-thead>
                                <b-tbody>
                                    <b-tr>
                                        <b-td><b>Total</b></b-td>
                                        <b-td>
                                            <b-input-group size="sm" append="dwt">
                                                <b-form-input
                                                        type="number"
                                                        @click="$event.target.select()"
                                                        v-model.number="itemDescription.item.scale_dwt"></b-form-input>
                                            </b-input-group>
                                        </b-td>
                                        <b-td>
                                            $ {{ _totalEAV }}
                                        </b-td>
                                    </b-tr>
                                    <b-tr v-for="(item, index) in itemDescription.itemComponents" :key="index"
                                          v-if="item.category_id || item.class_id">
                                        <b-td>{{ getClassName(item) | titleCase }}</b-td>
                                        <b-td>{{ item.aggregate_dwt | toFixed2 }}</b-td>
                                        <b-td v-if="item.category_id === 2">${{ item.numismatic_value | toFixed2 }}</b-td>
                                        <b-td v-else>${{ item.numismatic_value | toFixed2 }}</b-td>
                                    </b-tr>

                                </b-tbody>
                            </b-table-simple>

                        </b-card>
                    </b-col>

                    <b-col cols="7">
                        <b-card header="Item Description" header-class="font-weight-bold">
                            <b-form-row class="mb-3">
                                <b-col>
                                    <b-button-toolbar justify>
                                        <b-button size="sm" class="px-2 py-1" pill @click="selectTypeShortcut(12)">
                                            Ring
                                        </b-button>
                                        <b-button size="sm" class="px-2 py-1" pill @click="selectTypeShortcut(9)">
                                            Necklace
                                        </b-button>
                                        <b-button size="sm" class="px-2 py-1" pill @click="selectTypeShortcut(6)">
                                            Earring
                                        </b-button>
                                        <b-button size="sm" class="px-2 py-1" pill @click="selectTypeShortcut(10)">
                                            Other Jewelery
                                        </b-button>
                                        <b-button size="sm" class="px-2 py-1" pill @click="selectTypeShortcut(1)">
                                            Bracelet/Anklet
                                        </b-button>
                                        <b-button size="sm" class="px-2 py-1" pill @click="selectTypeShortcut(11)">
                                            Pendant
                                        </b-button>
                                        <b-button size="sm" class="px-2 py-1" pill @click="selectTypeShortcut(4)">
                                            Charm
                                        </b-button>
                                        <b-button size="sm" class="px-2 py-1" pill @click="selectTypeShortcut(14)">
                                            Coin
                                        </b-button>
                                    </b-button-toolbar>
                                </b-col>

                            </b-form-row>

                            <!-- COIN -->
                            <template v-if="isCoin(itemDescription.item)">
                                <!--METAL-->
                                <b-form-row class="mb-3" v-if="_isMetal">
                                    <b-col>
                                        <div class="d-flex justify-content-end">
                                            <label size="sm" class="font-weight-bold mr-3">Metal:</label>
                                            <b-form-radio-group
                                                    v-model="itemDescription.itemComponents[0].class_id">
                                                <b-form-radio size="sm"
                                                              name="metals_selection"
                                                              v-model.number="itemDescription.itemComponents[0].class_id"
                                                              value="1">
                                                    <span class="label">Gold</span>
                                                </b-form-radio>
                                                <b-form-radio size="sm"
                                                              name="metals_selection"
                                                              v-model.number="itemDescription.itemComponents[0].class_id"
                                                              value="4"><span class="label">Silver</span>
                                                </b-form-radio>
                                                <b-form-radio size="sm"
                                                              name="metals_selection"
                                                              v-model.number="itemDescription.itemComponents[0].class_id"
                                                              value="6">
                                                    <span class="label">Platinum</span>
                                                </b-form-radio>
                                                <b-form-radio size="sm"
                                                              name="metals_selection"
                                                              v-model.number="itemDescription.itemComponents[0].class_id"
                                                              value="7">
                                                    <span class="label">Palladium</span>
                                                </b-form-radio>
                                                <b-form-radio size="sm"
                                                              name="metals_selection"
                                                              v-model.number="itemDescription.itemComponents[0].class_id"
                                                              value="3">
                                                    <span class="label">G.Filled</span>
                                                </b-form-radio>
                                                <b-form-radio size="sm"
                                                              name="metals_selection"
                                                              v-model.number="itemDescription.itemComponents[0].class_id"
                                                              value="11"><span class="label">Other</span>
                                                </b-form-radio>
                                            </b-form-radio-group>
                                        </div>

                                    </b-col>
                                </b-form-row>

                                <!--TYPE-->
                                <b-form-row class="mb-3">
                                    <b-col cols="2" class="text-right">
                                        <label for="input-type" size="sm" class="font-weight-bold">Region:</label>
                                    </b-col>
                                    <b-col :cols=" _itemTypeCategoryName ? 5 : 10">
                                        <multiselect v-model="itemDescription.item.selected_type"
                                                     :options="getItemOptions()"
                                                     :optionHeight="10" group-values="types" group-label="name"
                                                     :searchable="true" :close-on-select="true" :show-labels="false"
                                                     :group-select="false" track-by="id" label="name"
                                                     placeholder="Pick a type"></multiselect>
                                    </b-col>
                                    <b-col cols="2" class="text-right" v-if="_itemTypeCategoryName">
                                        <label size="sm" class="font-weight-bold">Category:</label>
                                    </b-col>
                                    <b-col cols="3" class="text-left" v-if="_itemTypeCategoryName">
                                        &nbsp;{{ _itemTypeCategoryName || titleCase }}
                                        <b-button size="sm" @click="resetType" class="float-right">Reset</b-button>
                                    </b-col>
                                </b-form-row>

                                <!--PURITY & COLOR-->
                                <b-form-row class="mb-3"
                                            v-if="!isMelee(itemDescription.itemComponents[0]) && !isDiamond(itemDescription.itemComponents[0]) && !isPreciousStone(itemDescription.itemComponents[0]) && !isGem(itemDescription.itemComponents[0]) && !isSyntheticStone(itemDescription.itemComponents[0]) && !isPearls(itemDescription.itemComponents[0])">

                                    <b-col cols="2" class="text-right">
                                        <label for="input-purity"
                                               size="sm"
                                               class="font-weight-bold">Purity:</label>
                                    </b-col>
                                    <b-col>
                                        <b-row>
                                            <b-col cols="7" class="pr-1">
                                                <b-form-input size="sm"
                                                              type="number"
                                                              :class="itemDescription.itemComponents[0].purity === null || itemDescription.itemComponents[0].purity < 0 || ((itemDescription.itemComponents[0].purity_type == 'kt' ? 24.01 : 100.01) < itemDescription.itemComponents[0].purity)|| itemDescription.itemComponents[0].purity === '' ? 'input-warning' : null"
                                                              v-model.number="itemDescription.itemComponents[0].purity">
                                                </b-form-input>
                                            </b-col>
                                            <b-col class="pl-0">
                                                <b-form-select v-model="itemDescription.itemComponents[0].purity_type"
                                                               :class="!itemDescription.itemComponents[0].purity_type ? 'input-warning' : null"
                                                               size="sm">
                                                    <b-form-select-option value="kt" v-if="_isGoldType">kt
                                                    </b-form-select-option>
                                                    <b-form-select-option value="%">%
                                                    </b-form-select-option>
                                                </b-form-select>
                                            </b-col>
                                        </b-row>
                                    </b-col>

                                    <b-col cols="7" class="mb-2 no-gutters" v-if="_displayStyle">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="2" class="text-right">
                                                <label for="input-style" size="sm"
                                                       class="font-weight-bold">Coin: <i class="text-primary fa-solid fa-coins" id="coin-image-target" @mouseover="getCoinImage(coinDropDownSelection)"></i></label>
                                            </b-col>
                                            <b-col>
                                                <div id="coin-select-dropdown-wrapper">
                                                    <multiselect v-model="coinDropDownSelection"
                                                                 :class="!coinDropDownSelection ? 'input-warning' : null"
                                                                 id="coin-select-dropdown"
                                                                 :options="_sortedCoins"
                                                                 :searchable="true"
                                                                 track-by="text"
                                                                 label="text"
                                                                 :close-on-select="true"
                                                                 :show-labels="false"
                                                                 @SearchChange="showCoinPopover = false"
                                                                 @open="coinSelectIsOpen()"
                                                                 ref="coinSelection"
                                                                 @close="coinDropDownOpen = false; getCoinImage(coinDropDownSelection);"
                                                                 @select="getCoinImage(coinDropDownSelection)"
                                                                 @input="setCoinStyleType(coinDropDownSelection)"
                                                                 placeholder="Select Coin">
                                                    </multiselect>
                                                </div>
                                                <b-popover v-if="selectedCoin && selectedCoin.image_url" :show.sync="showCoinPopover"
                                                           target="coin-image-target" triggers="hover" placement="left">
                                                    <template #title>
                                                        <small class="font-weight-bold">{{
                                                                selectedCoin.series
                                                            }}</small>
                                                    </template>
                                                    <div class="flip-container">
                                                        <div class="flipper">
                                                            <div class="front">
                                                                <div class="coin-image"
                                                                     :style="{ backgroundImage: `url(${ selectedCoin.image_url })` }"></div>
                                                            </div>
                                                            <div class="back">
                                                                <div class="coin-image"
                                                                     :style="{ backgroundImage: `url(${ selectedCoin.image_url })` }"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </b-popover>
                                            </b-col>
                                        </b-form-row>
                                    </b-col>

                                </b-form-row>


                                <b-form-row class="mb-3 pl-4" v-if="coinDropDownSelection && coinDropDownSelection.text === 'Other'">
                                    <b-col class="text-right">
                                        <label for="appraiser-notes" size="sm" class="font-weight-bold label">Coin Type Description:</label>
                                    </b-col>
                                    <b-col cols="6">
                                        <b-form-input
                                                id="appraiser-notes"
                                                v-model="itemDescription.item.appraiser_notes"
                                                size="sm"
                                        ></b-form-input>
                                    </b-col>
                                </b-form-row>


                                <!--QUANTITY & CONDITION-->
                                <b-form-row class="mb-3"
                                            v-if="!isMelee(itemDescription.itemComponents[0]) && !isDiamond(itemDescription.itemComponents[0]) && !isPreciousStone(itemDescription.itemComponents[0]) && !isGem(itemDescription.itemComponents[0]) && !isSyntheticStone(itemDescription.itemComponents[0])">

                                    <b-col cols="2" class="text-right">
                                        <label for="input-quantity" size="sm" class="font-weight-bold">Quantity:</label>
                                    </b-col>
                                    <b-col>
                                        <b-form-input id="input-quantity" size="sm"
                                                      @click="$event.target.select()"
                                                      v-model.number="itemDescription.itemComponents[0].quantity"
                                                      type="number"></b-form-input>
                                    </b-col>


                                    <b-col cols="2" class="text-right">
                                        <label for="input-color" size="sm" class="font-weight-bold">Color:</label>
                                    </b-col>
                                    <b-col>
                                        <b-form-select id="input-color" size="sm" :disabled="_disabledColor"
                                                       :class="!itemDescription.itemComponents[0].color_id && itemDescription.itemComponents[0].class_id ? 'input-warning' : null"

                                                       v-model="itemDescription.itemComponents[0].color_id"
                                                       :options="itemDescription.options.colors"
                                        ></b-form-select>
                                    </b-col>

                                </b-form-row>


                                <!--ENGRAVING, STYLE, BRAND, AND LENGTH (when it shows up)-->
                                <b-row class="row-padding">

                                    <b-col cols="6" class="mb-3 no-gutters" v-if="_displayEngravings">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="4" class="text-right">
                                                <label for="input-engravings" size="sm" class="font-weight-bold">Engravings/<br>Serial:</label>
                                            </b-col>
                                            <b-col cols="8">
                                                <b-form-input id="input-serial" size="sm" @click="$event.target.select()"
                                                              v-model="itemDescription.item.serial_number">

                                                </b-form-input>
                                            </b-col>
                                        </b-form-row>
                                    </b-col>


                                    <b-col cols="6" class="mb-3 no-gutters" v-if="_displayReferenceNumber">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="4" class="text-right">
                                                <label for="input-size" size="sm" class="font-weight-bold">Reference
                                                    Number:</label>
                                            </b-col>
                                            <b-col cols="8">
                                                <b-form-input id="input-size" size="sm"
                                                              v-model="itemDescription.item.reference_number">
                                                </b-form-input>
                                            </b-col>
                                        </b-form-row>
                                    </b-col>

                                    <b-col cols="6" class="mb-3 no-gutters">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="4" class="text-right">
                                                <label for="input-size" size="sm" class="font-weight-bold">Certificate
                                                    Number:</label>
                                            </b-col>
                                            <b-col cols="8">
                                                <b-form-input id="input-size" size="sm" placeholder="(for certified coins)"
                                                              v-model="itemDescription.item.certificate_number">
                                                </b-form-input>
                                            </b-col>
                                        </b-form-row>
                                    </b-col>


                                    <b-col cols="6" class="mb-3 no-gutters">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="4" class="text-right">
                                                <label for="input-condition" size="sm"
                                                       class="font-weight-bold">Condition:</label>
                                            </b-col>
                                            <b-col>
                                                <b-form-select id="input-condition" size="sm"
                                                               v-model="itemDescription.item.condition_id"
                                                               :options="itemDescription.options.conditions"
                                                ></b-form-select>
                                            </b-col>
                                        </b-form-row>

                                    </b-col>

                                    <!-- on child zero only
                                                                    <b-col cols="6" class="mb-3 no-gutters" v-if="isDiamond(itemDescription.itemComponents[0])">
                                                                        <b-form-row class="extra-option-rows-padding">
                                                                            <b-col cols="4" class="text-right">
                                                                                <label for="input-condition" size="sm"
                                                                                       class="font-weight-bold">Certificate:</label>
                                                                            </b-col>
                                                                            <b-col>
                                                                                <b-form-select id="input-condition" size="sm"
                                                                                               v-model="itemDescription.item.certificate_type_id"
                                                                                               :options="itemDescription.options.certificates"
                                                                                ></b-form-select>
                                                                            </b-col>
                                                                        </b-form-row>
                                                                    </b-col>

                                                                    <b-col cols="6" class="mb-3 no-gutters" v-if=" isDiamond(itemDescription.itemComponents[0])">
                                                                        <b-form-row class="extra-option-rows-padding">
                                                                            <b-col cols="4" class="text-right">
                                                                                <label for="input-engravings" size="sm" class="font-weight-bold">Certificate
                                                                                    Number:</label>
                                                                            </b-col>
                                                                            <b-col cols="8">
                                                                                <b-form-input id="input-serial" size="sm"
                                                                                              v-model="itemDescription.item.certificate_number">

                                                                                </b-form-input>
                                                                            </b-col>
                                                                        </b-form-row>
                                                                    </b-col>
                                                                    -->
                                </b-row>

                            </template>

                            <!-- NON-COIN -->
                            <template v-else>
                                <!--TYPE-->
                                <b-form-row class="mb-3">
                                    <b-col cols="2" class="text-right">
                                        <label for="input-type" size="sm" class="font-weight-bold">Type:</label>
                                    </b-col>
                                    <b-col :cols=" _itemTypeCategoryName ? 5 : 10">
                                        <multiselect v-model="itemDescription.item.selected_type"
                                                     :options="getItemOptions()"
                                                     :optionHeight="10" group-values="types" group-label="name"
                                                     :searchable="true" :close-on-select="true" :show-labels="false"
                                                     :group-select="false" track-by="id" label="name"
                                                     placeholder="Pick a type"></multiselect>
                                    </b-col>
                                    <b-col cols="2" class="text-right" v-if="_itemTypeCategoryName">
                                        <label size="sm" class="font-weight-bold">Category:</label>
                                    </b-col>
                                    <b-col cols="3" class="text-left" v-if="_itemTypeCategoryName">
                                        &nbsp;{{ _itemTypeCategoryName || titleCase }}
                                    </b-col>
                                </b-form-row>


                                <!--METAL-->
                                <b-form-row class="mb-3" v-if="_isMetal">
                                    <b-col>
                                        <div class="d-flex justify-content-end">
                                            <label size="sm" class="font-weight-bold mr-3">Metal:</label>
                                            <b-form-radio-group
                                                    v-model="itemDescription.itemComponents[0].class_id">
                                                <b-form-radio size="sm"
                                                              name="metals_selection"
                                                              v-model.number="itemDescription.itemComponents[0].class_id"
                                                              value="1"><span class="label">Gold</span>
                                                </b-form-radio>
                                                <b-form-radio size="sm"
                                                              name="metals_selection"
                                                              v-model.number="itemDescription.itemComponents[0].class_id"
                                                              value="4"><span class="label">Silver</span>
                                                </b-form-radio>
                                                <b-form-radio size="sm"
                                                              name="metals_selection"
                                                              v-model.number="itemDescription.itemComponents[0].class_id"
                                                              value="6">
                                                    <span class="label">Platinum</span>
                                                </b-form-radio>
                                                <b-form-radio size="sm"
                                                              name="metals_selection"
                                                              v-model.number="itemDescription.itemComponents[0].class_id"
                                                              value="7">
                                                    <span class="label">Palladium</span>
                                                </b-form-radio>
                                                <b-form-radio size="sm"
                                                              name="metals_selection"
                                                              v-model.number="itemDescription.itemComponents[0].class_id"
                                                              value="3"><span class="label">G.Filled</span>
                                                </b-form-radio>
                                                <b-form-radio size="sm"
                                                              name="metals_selection"
                                                              v-model.number="itemDescription.itemComponents[0].class_id"
                                                              value="11"><span class="label">Other</span>
                                                </b-form-radio>
                                            </b-form-radio-group>
                                        </div>
                                    </b-col>
                                </b-form-row>


                                <!--PURITY & COLOR-->
                                <b-form-row class="mb-3"
                                            v-if="!isMelee(itemDescription.itemComponents[0]) && !isDiamond(itemDescription.itemComponents[0]) && !isPreciousStone(itemDescription.itemComponents[0]) && !isGem(itemDescription.itemComponents[0]) && !isSyntheticStone(itemDescription.itemComponents[0]) && !isPearls(itemDescription.itemComponents[0])">

                                    <b-col cols="2" class="text-right">
                                        <label for="input-purity"
                                               size="sm"
                                               class="font-weight-bold">Purity:</label>
                                    </b-col>
                                    <b-col>
                                        <b-form-select
                                                :class="!_itemPurity && itemDescription.item.selected_type ? 'input-warning' : null"
                                            id="input-purity" size="sm"
                                            v-model="_itemPurity"
                                            :options="itemDescription.options.purities"
                                        >
                                        </b-form-select>
                                        <b-row>
                                            <b-col>
                                                <b-form-input size="sm"
                                                              :class="(itemDescription.itemComponents[0].purity === null || itemDescription.itemComponents[0].purity === '' || itemDescription.itemComponents[0].purity < 0 || (itemDescription.itemComponents[0].purity_type == 'kt' ? 24.01 : 100.01) < itemDescription.itemComponents[0].purity) && itemDescription.item.selected_type ? 'input-warning' : null"
                                                              v-model.number="itemDescription.itemComponents[0].purity"
                                                              type="number"
                                                              v-if="_itemPurity === 'other'">
                                                </b-form-input>
                                            </b-col>
                                            <b-col>
                                                <b-form-select v-model="itemDescription.itemComponents[0].purity_type"
                                                               :class="!itemDescription.itemComponents[0].purity_type && itemDescription.item.selected_type ? 'input-warning' : null"
                                                               size="sm"
                                                               v-if="_itemPurity === 'other'">
                                                    <b-form-select-option value="kt" v-if="_isGoldType">kt
                                                    </b-form-select-option>
                                                    <b-form-select-option value="%" v-if="!_isGoldType">%
                                                    </b-form-select-option>
                                                </b-form-select>
                                            </b-col>
                                        </b-row>
                                    </b-col>

                                    <b-col cols="2" class="text-right">
                                        <label for="input-color" size="sm" class="font-weight-bold">Color:</label>
                                    </b-col>
                                    <b-col>
                                        <b-form-select id="input-color" size="sm" :disabled="_disabledColor"
                                                       :class="!itemDescription.itemComponents[0].color_id && itemDescription.item.selected_type ? 'input-warning' : null"
                                                       v-model="itemDescription.itemComponents[0].color_id"
                                                       :options="itemDescription.options.colors"
                                        ></b-form-select>
                                    </b-col>

                                </b-form-row>

                                <!--QUANTITY & CONDITION-->
                                <b-form-row class="mb-3"
                                            v-if="!isMelee(itemDescription.itemComponents[0]) && !isDiamond(itemDescription.itemComponents[0]) && !isPreciousStone(itemDescription.itemComponents[0]) && !isGem(itemDescription.itemComponents[0]) && !isSyntheticStone(itemDescription.itemComponents[0]) && !isPearls(itemDescription.itemComponents[0])">

                                    <b-col cols="2" class="text-right">
                                        <label for="input-quantity" size="sm" class="font-weight-bold">Quantity:</label>
                                    </b-col>
                                    <b-col>
                                        <b-form-input id="input-quantity" size="sm"
                                                      @click="$event.target.select()"
                                                      v-model="itemDescription.item.quantity"
                                                      :class="((itemDescription.item.quantity) < 1) ? 'input-warning' : null"
                                                      min="1"
                                                      type="number"></b-form-input>
                                    </b-col>


                                    <b-col cols="2" class="text-right">
                                        <label for="input-condition" size="sm"
                                               class="font-weight-bold">Condition:</label>
                                    </b-col>
                                    <b-col>
                                        <b-form-select id="input-condition" size="sm"
                                                       v-model="itemDescription.item.condition_id"
                                                       :options="itemDescription.options.conditions"
                                        ></b-form-select>
                                    </b-col>

                                </b-form-row>


                                <!--ENGRAVING, STYLE, BRAND, AND LENGTH (when it shows up)-->
                                <b-row class="row-padding">
                                    <b-col cols="6" class="mb-3 no-gutters" v-if="_displayStyle">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="4" class="text-right">
                                                <label for="input-style" size="sm"
                                                       class="font-weight-bold">Style:</label>
                                            </b-col>
                                            <b-col>
                                                <b-form-select id="input-style" size="sm"
                                                               :class="!itemDescription.itemComponents[0].style_id && itemDescription.item.selected_type ? 'input-warning' : null "
                                                               v-model="itemDescription.itemComponents[0].style_id"
                                                               :options="itemDescription.options.styles"
                                                ></b-form-select>
                                            </b-col>
                                        </b-form-row>
                                    </b-col>
                                    <b-col cols="6" class="mb-3 no-gutters" v-if="_displayEngravings">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="4" class="text-right">
                                                <label for="input-engravings" size="sm" class="font-weight-bold">Engravings/<br>Serial:</label>
                                            </b-col>
                                            <b-col cols="8">
                                                <b-form-input id="input-serial" size="sm"
                                                              @click="$event.target.select()"
                                                              v-model="itemDescription.item.serial_number">

                                                </b-form-input>
                                            </b-col>
                                        </b-form-row>
                                    </b-col>
                                    <b-col cols="6" class="mb-3 no-gutters" v-if="_displayBrand">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="4" class="text-right">
                                                <label for="input-brand"
                                                       size="sm"
                                                       class="font-weight-bold">Brand:</label>
                                            </b-col>
                                            <b-col>
                                                <b-form-select
                                                    id="input-brand" size="sm"
                                                    :class="!itemDescription.item.brand_id && (itemDescription.item.selected_type && itemDescription.item.selected_type.item_category_id == 3) ? 'input-warning' : null"
                                                    v-model="itemDescription.item.brand_id"
                                                    :options="itemDescription.options.brands"
                                                >
                                                </b-form-select>
                                            </b-col>
                                        </b-form-row>
                                    </b-col>
                                    <!-- Ring -->

                                    <b-col cols="6" class="mb-3 no-gutters" v-if="_display_size">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="4" class="text-right">
                                                <label for="input-size" size="sm" class="font-weight-bold">Size:</label>
                                            </b-col>
                                            <b-col cols="8">
                                                <b-form-select v-model="_itemSize" :options="sizeOptionsByItemTypeId[this.itemDescription.item.selected_type.id]">
                                                    <b-form-select-option value="other">Other</b-form-select-option>
                                                </b-form-select>
                                                <b-form-input type="number" id="input-size" size="sm" min="3" max="20"
                                                              step=".25"
                                                              v-model.number="itemDescription.item.size"
                                                              v-if="_itemSize === 'other'">
                                                </b-form-input>
                                            </b-col>
                                        </b-form-row>
                                    </b-col>
                                    <!-- Neckless and anklet-->

                                    <b-col cols="6" class="mb-3 no-gutters" v-if="_display_length">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="4" class="text-right">
                                                <label for="input-size" size="sm"
                                                       class="font-weight-bold">Length:</label>
                                            </b-col>
                                            <b-col cols="8">
                                                <b-form-select v-model="_itemSize" :options="sizeOptionsByItemTypeId[this.itemDescription.item.selected_type.id]">
                                                    <b-form-select-option value="other">Other</b-form-select-option>
                                                </b-form-select>
                                                <b-form-input type="number" id="input-size" size="sm"
                                                              v-model.number="itemDescription.item.size"
                                                              v-if="_itemSize === 'other'">
                                                </b-form-input>
                                            </b-col>
                                        </b-form-row>
                                    </b-col>

                                    <b-col cols="6" class="mb-3 no-gutters" v-if="_displayReferenceNumber">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="4" class="text-right">
                                                <label for="input-size" size="sm" class="font-weight-bold">Reference
                                                    Number:</label>
                                            </b-col>
                                            <b-col cols="8">
                                                <b-form-input id="input-size" size="sm"
                                                              v-model="itemDescription.item.reference_number">
                                                </b-form-input>
                                            </b-col>
                                        </b-form-row>
                                    </b-col>
                                    <b-col cols="6" class="mb-3 no-gutters" v-if="_displayBoxAndPapers">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="4" class="text-right">
                                                <label for="input-size" size="sm" class="font-weight-bold">Box And
                                                    Papers:</label>
                                            </b-col>
                                            <b-col cols="8">
                                                <b-form-select size="sm"
                                                               :options="componentOptions.boxAndPaperOptions"
                                                               v-model="itemDescription.item.box_and_papers"
                                                ></b-form-select>
                                            </b-col>
                                        </b-form-row>
                                    </b-col>

                                    <b-col cols="6" class="mb-3 no-gutters" v-if="_displayConditionNonCore">
                                        <b-form-row class="extra-option-rows-padding">
                                            <b-col cols="4" class="text-right">
                                                <label for="input-condition" size="sm"
                                                       class="font-weight-bold">Condition:</label>
                                            </b-col>
                                            <b-col>
                                                <b-form-select id="input-condition" size="sm"
                                                               v-model="itemDescription.itemComponents[0].condition_id"
                                                               :options="itemDescription.options.conditions"
                                                ></b-form-select>
                                            </b-col>
                                        </b-form-row>

                                    </b-col>

                                    <!-- on child zero only
                                                                    <b-col cols="6" class="mb-3 no-gutters" v-if="isDiamond(itemDescription.itemComponents[0])">
                                                                        <b-form-row class="extra-option-rows-padding">
                                                                            <b-col cols="4" class="text-right">
                                                                                <label for="input-condition" size="sm"
                                                                                       class="font-weight-bold">Certificate:</label>
                                                                            </b-col>
                                                                            <b-col>
                                                                                <b-form-select id="input-condition" size="sm"
                                                                                               v-model="itemDescription.item.certificate_type_id"
                                                                                               :options="itemDescription.options.certificates"
                                                                                ></b-form-select>
                                                                            </b-col>
                                                                        </b-form-row>
                                                                    </b-col>

                                                                    <b-col cols="6" class="mb-3 no-gutters" v-if=" isDiamond(itemDescription.itemComponents[0])">
                                                                        <b-form-row class="extra-option-rows-padding">
                                                                            <b-col cols="4" class="text-right">
                                                                                <label for="input-engravings" size="sm" class="font-weight-bold">Certificate
                                                                                    Number:</label>
                                                                            </b-col>
                                                                            <b-col cols="8">
                                                                                <b-form-input id="input-serial" size="sm"
                                                                                              v-model="itemDescription.item.certificate_number">

                                                                                </b-form-input>
                                                                            </b-col>
                                                                        </b-form-row>
                                                                    </b-col>
                                                                    -->
                                </b-row>

                            </template>

                            <!--TEST METHODS-->
                            <b-form-row class="my-3">
                                <b-col cols="2" class="text-right">
                                    <label size="sm" class="font-weight-bold">Test Method:</label>
                                </b-col>


                                <b-col cols="2">
                                    <b-form-group class="text-center" label-for="test-xrf"
                                                  label-class="font-weight-bold mb-1 label">
                                        <template v-slot:label>
                                            <i class="text-warning mr-1 fa-solid fa-bolt"></i><span class="label">XRF</span>
                                        </template>
                                        <b-form-checkbox
                                            id="test-xrf"
                                            v-model="itemDescription.item.verificationMethods.xrf"
                                            name="check-button"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="2">
                                    <b-form-group class="text-center" label-for="test-acid"
                                                  label-class="font-weight-bold mb-1 label">
                                        <template v-slot:label>
                                            <i class="text-success mr-1 fa-solid fa-flask"></i><span
                                            class="label">Acid</span>
                                        </template>
                                        <b-form-checkbox
                                            id="test-acid"
                                            v-model="itemDescription.item.verificationMethods.acid"
                                            name="check-button"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="2">
                                    <b-form-group class="text-center" label-for="test-magnet"
                                                  label-class="font-weight-bold mb-1 label">
                                        <template v-slot:label>
                                            <i class="text-danger mr-1 fa-solid fa-magnet"></i><span
                                            class="label">Magnet</span>
                                        </template>
                                        <b-form-checkbox
                                            id="test-magnet"
                                            v-model="itemDescription.item.verificationMethods.magnet"
                                            name="check-button"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="2">
                                    <b-form-group class="text-center" label-for="test-hallmark"
                                                  label-class="font-weight-bold mb-1 label">
                                        <template v-slot:label>
                                            <i class="text-primary mr-1 fa-solid fa-h-square"></i><span
                                            class="label">Hallmark</span>
                                        </template>
                                        <b-form-checkbox
                                            id="test-hallmark"
                                            v-model="itemDescription.item.verificationMethods.hallmark"
                                            name="check-button"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="2">
                                    <b-form-group class="text-center" label-for="test-other"
                                                  label-class="font-weight-bold mb-1 label">
                                        <template v-slot:label>
                                            <i class="mr-1 fa-solid fa-map-marker-question"></i><span
                                            class="label">Other</span>
                                        </template>
                                        <b-form-checkbox
                                            id="test-other"
                                            v-model="itemDescription.item.verificationMethods.other"
                                            name="check-button"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                            </b-form-row>

                            <!--MELT HOLD-->
                            <b-form-row class="mb-2">
                                <b-col cols="2" class="text-right">
                                    <label size="sm" class="font-weight-bold">Melt/Hold:</label>
                                </b-col>
                                <b-col cols="2">
                                    <b-form-group v-model.number="itemDescription.item.inventory_status_id">
                                        <b-form-radio v-model.number="itemDescription.item.inventory_status_id"
                                                      value="1"><span class="label">Melt</span>
                                        </b-form-radio>
                                        <b-form-radio v-model.number="itemDescription.item.inventory_status_id"
                                                      value="2"><span class="label">Hold</span>
                                        </b-form-radio>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="4">
                                    <b-form-group
                                        label-size="sm"
                                        label-class="font-weight-bold label"
                                        label-for="additional-packaging"
                                        label="Additional Packaging:"
                                        label-align-lg="right"
                                        label-cols="6">
                                        <template v-slot:label>
                                            <span class="label">Additional Packaging:</span>
                                        </template>
                                        <b-form-checkbox
                                            id="additional-packaging"
                                            v-model="itemDescription.item.additional_packaging"
                                            name="check-button"
                                            class="mt-2"
                                            switch>
                                        </b-form-checkbox>
                                    </b-form-group>
                                </b-col>
                                <b-col>
                                    <b-form-group v-if="isCoin(itemDescription.item)"
                                                  label-size="sm"
                                                  label-class="font-weight-bold label"
                                                  label-for="item-eav"
                                                  label-align="right"
                                                  label="EAV:"
                                                  label-cols="3">
                                        <b-form-input size="sm" id="item-eav"
                                                      type="number"
                                                      :formatter="$inputSanitizer.toNumeric"
                                                      lazy-formatter
                                                      v-model.number="itemDescription.itemComponents[0].numismatic_value"
                                                      @click="$event.target.select()"
                                                      :disabled="preventEAVEdit || order.status_name === 'Quality Assurance'"
                                        ></b-form-input>
                                    </b-form-group>
                                    <b-form-group v-else
                                                  label-size="sm"
                                                  label-class="font-weight-bold label"
                                                  label-for="item-eav"
                                                  label-align="right"
                                                  label="EAV:"
                                                  label-cols="3">
                                        <b-form-input size="sm" id="item-eav"
                                                      type="number"
                                                      :formatter="$inputSanitizer.toNumeric"
                                                      lazy-formatter
                                                      v-model.number="itemDescription.item.numismatic_value"
                                                      @click="$event.target.select()"
                                                      :disabled="preventEAVEdit"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>

                            </b-form-row>
                            <b-form-row class="mb-2" v-if="itemDescription.item.inventory_status_id == 2">
                                <b-col cols="3">
                                    <label size="sm" class="font-weight-bold">Hold Bag Type:</label>
                                </b-col>
                                <b-col cols="4">
                                    <b-select v-model="hold_bag_id" :options="bagOptions"
                                              class="font-weight-bold text-center"></b-select>
                                </b-col>
                            </b-form-row>
                        </b-card>


                        <template>
                            <div role="tablist">

                                <!-- ADDITIONS -->
                                <b-card no-body class="sub-add-accordion header-sm mb-2">
                                    <b-card-header header-tag="header" class="p-0" role="tab">
                                        <b-button variant="light" block href="#" v-b-toggle.additions
                                                  class="text-left">
                                            <span class="label">Value Additions</span>

                                            <b-button size="sm" pill class="ml-3 mr-1" @click.stop.prevent="addMelee"><i
                                                class="fa-regular fa-plus"></i> Melee
                                            </b-button>
                                            <b-button size="sm" pill class="mr-1" @click.stop.prevent="addDiamond"><i
                                                class="fa-regular fa-plus"></i> Diamond
                                            </b-button>
                                            <b-button size="sm" pill class="mr-1"
                                                      @click.stop.prevent="addPreciousStone"><i class="fa-regular fa-plus"></i>
                                                Precious Stone
                                            </b-button>
                                            <b-button size="sm" pill class="mr-1" @click.stop.prevent="addMixedMetal"><i
                                                class="fa-regular fa-plus"></i> Mixed Metal
                                            </b-button>

                                        </b-button>
                                    </b-card-header>
                                    <b-collapse id="additions" role="tabpanel" v-model="additionVisible">
                                        <b-list-group flush>

                                            <!--Melee-->
                                            <template ref="melee"
                                                      v-for="(item, index) in itemDescription.itemComponents"
                                            >
                                                <b-list-group-item :key="index" v-if="isMelee(item)">
                                                    <b-row class="child child-melee child-allow-multiple">
                                                        <b-col>
                                                            <i class="fa-solid fa-th fa-lg"></i> Melee
                                                        </b-col>
                                                        <b-col class="text-right">
                                                            <b-button variant="link" size="sm" v-if="index > 0"
                                                                      @click="deleteItem(index)"><i
                                                                class="fa-light fa-trash-alt"></i></b-button>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>

                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Qty:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              v-model.number="item.quantity"
                                                                              :class="((item.quantity) < 1) ? 'input-warning' : null"
                                                                              min="1"
                                                                              @click="$event.target.select()"
                                                                              @change="calculateItemComponentDWT(item)">
                                                                </b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                    label-cols="5"
                                                                    label="Points (ea.):"
                                                                    label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm" min="0"
                                                                              @click="$event.target.select()"
                                                                              v-model.number="item.tare_weight"
                                                                              :class="((item.tare_weight) < 0) ? 'input-warning' : null"
                                                                              @change="calculateItemComponentDWT(item)">
                                                                </b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="EAV:"
                                                                label-size="sm"
                                                            >
                                                                <b-input-group>
                                                                    <b-form-input type="number" size="sm"
                                                                                  @click="$event.target.select()"
                                                                                  v-model="item.numismatic_value"
                                                                                  :formatter="$inputSanitizer.toNumeric"
                                                                                  lazy-formatter
                                                                                  :disabled="preventEAVEdit"
                                                                    ></b-form-input>
                                                                    <b-input-group-append>
                                                                        <b-button size="sm" variant="outline-success"
                                                                                  @click="getDiamondEAVValue(item)">Get
                                                                        </b-button>
                                                                    </b-input-group-append>
                                                                </b-input-group>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Color:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="componentOptions.melee.colors"
                                                                               v-model="item.color_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Clarity:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="componentOptions.melee.clarities"
                                                                               v-model="item.clarity_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>

                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Cut:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="componentOptions.melee.styles"
                                                                               v-model="item.style_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row v-if="index > 0">
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Brand:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="getDiamondBrands()"
                                                                               v-model="item.brand_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Condition:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="getDiamondConditions()"
                                                                               v-model="item.condition_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                </b-list-group-item>
                                            </template>
                                            <!--End Melee-->

                                            <!--Diamond-->
                                            <template ref="diamond"
                                                      v-for="(item, index) in itemDescription.itemComponents"
                                            >
                                                <b-list-group-item :key="index" v-if="isDiamond(item)">
                                                    <b-row class="child child-diamond child-allow-multiple">
                                                        <b-col>
                                                            <i class="fa-regular fa-gem fa-lg"></i> Diamond
                                                        </b-col>
                                                        <b-col class="text-right">
                                                            <b-button variant="link" size="sm"
                                                                      @click="deleteItem(index)" v-if="index > 0"><i
                                                                class="fa-light fa-trash-alt"></i></b-button>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>

                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Qty:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              v-model.number="item.quantity"
                                                                              :class="((item.quantity) < 1) ? 'input-warning' : null"
                                                                              min="1"
                                                                              @click="$event.target.select()"
                                                                              @change="calculateItemComponentDWT(item)">
                                                                </b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                    label-cols="7"
                                                                    label="Ct (ea):"
                                                                    label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm" placeholder="ct"
                                                                              step="0.01" min="0"
                                                                              @click="$event.target.select()"
                                                                              v-model.number="item.tare_weight"
                                                                              :class="((item.tare_weight) < 0) ? 'input-warning' : null"
                                                                              @change="calculateItemComponentDWT(item)">
                                                                </b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="EAV:"
                                                                label-size="sm"
                                                            >
                                                                <b-input-group>
                                                                    <b-form-input type="number" size="sm"
                                                                                  @click="$event.target.select()"
                                                                                  v-model="item.numismatic_value"
                                                                                  :formatter="$inputSanitizer.toNumeric"
                                                                                  lazy-formatter
                                                                                  :disabled="preventEAVEdit"
                                                                    ></b-form-input>
                                                                    <b-input-group-append>
                                                                        <b-button size="sm" variant="outline-success"
                                                                                  @click="getDiamondEAVValue(item)">Get
                                                                        </b-button>
                                                                    </b-input-group-append>
                                                                </b-input-group>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Color:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="componentOptions.diamond.colors"
                                                                               v-model="item.color_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Clarity:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="componentOptions.diamond.clarities"
                                                                               v-model="item.clarity_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>

                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Cut:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="componentOptions.diamond.styles"
                                                                               v-model="item.style_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Certificate:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="componentOptions.diamond.certificates"
                                                                               v-model="item.certificate_type_id"
                                                                >
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Report #:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="text" size="sm"
                                                                              @click="$event.target.select()"
                                                                              v-model="item.certificate_number"></b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row v-if="index > 0">
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Brand:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="getDiamondBrands()"
                                                                               v-model="item.brand_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Condition:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="getDiamondConditions()"
                                                                               v-model="item.condition_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                </b-list-group-item>
                                            </template>
                                            <!--End Diamond-->

                                            <!--Precious Stone-->
                                            <template ref="precious-stone"
                                                      v-for="(item, index) in itemDescription.itemComponents"
                                            >
                                                <b-list-group-item :key="index" v-if="isPreciousStone(item)">
                                                    <b-row class="child child-precious-stone child-allow-multiple">
                                                        <b-col>
                                                            <i class="fa-solid fa-gem fa-lg"></i> Precious Stone
                                                        </b-col>
                                                        <b-col class="text-right">
                                                            <b-button variant="link" size="sm"
                                                                      @click="deleteItem(index)" v-if="index > 0"><i
                                                                class="fa-light fa-trash-alt"></i></b-button>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Qty:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              v-model.number="item.quantity"
                                                                              :class="((item.quantity) < 1) ? 'input-warning' : null"
                                                                              min="1"
                                                                              @click="$event.target.select()"
                                                                              @change="calculateItemComponentDWT(item)"
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                    label-cols="7"
                                                                    :label="item.weight_type == 'dwt' ? 'Weight (dwt):':
                                                                  item.weight_type == 'carats' ? 'Ct (ea):' :
                                                                  'Points (ea.):' "
                                                                    label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm" placeholder="ct"
                                                                              v-model.number="item.tare_weight"
                                                                              :class="((item.tare_weight) < 0) ? 'input-warning' : null"
                                                                              @click="$event.target.select()"
                                                                              :step="item.weight_type == 'carats' ? .01 : 1"
                                                                              min="0"
                                                                              @change="calculateItemComponentDWT(item)"
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="EAV:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              @click="$event.target.select()"
                                                                              :disabled="preventEAVEdit"
                                                                              v-model="item.numismatic_value"
                                                                              :formatter="$inputSanitizer.toNumeric"
                                                                              lazy-formatter
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Type:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="componentOptions.precious_stone.types"
                                                                               v-model="item.type_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Color:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="getPreciousStoneColorByType(item.type_id)"
                                                                               v-model="item.color_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>

                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Cut:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="getPreciousStoneStylesByType(item.type_id)"
                                                                               v-model="item.style_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Certificate:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="componentOptions.diamond.certificates"
                                                                               v-model="item.certificate_type_id"
                                                                >
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Report #:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="text" size="sm" @click="$event.target.select()"
                                                                              v-model="item.certificate_number"></b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                </b-list-group-item>
                                            </template>
                                            <!--End Precious Stone-->

                                            <!--Mixed Metal-->
                                            <template ref="mixed-metal"
                                                      v-for="(item, index) in itemDescription.itemComponents"
                                            >
                                                <b-list-group-item :key="index" v-if="isMixedMetal(item) && index != 0">
                                                    <b-row class="child child-mixed-metal child-allow-multiple">
                                                        <b-col>
                                                            <i class="fa-solid fa-shield-alt fa-lg"></i> Mixed Metal
                                                        </b-col>
                                                        <b-col class="text-right">
                                                            <b-button variant="link" size="sm" v-if="index > 0"
                                                                      @click="deleteItem(index)"><i
                                                                class="fa-light fa-trash-alt"></i></b-button>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="2"
                                                                label="2nd Metal:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-radio-group
                                                                    v-model="item.class_id"
                                                                    v-on:input="setMixedMetalPurityOptions(index, $event)">
                                                                    <b-form-radio size="sm"
                                                                                  :name="'metals_selection'+index"
                                                                                  v-model.number="item.class_id"
                                                                                  value="1">Gold
                                                                    </b-form-radio>
                                                                    <b-form-radio size="sm"
                                                                                  :name="'metals_selection'+index"
                                                                                  v-model.number="item.class_id"
                                                                                  value="4">Silver
                                                                    </b-form-radio>
                                                                    <b-form-radio size="sm"
                                                                                  :name="'metals_selection'+index"
                                                                                  v-model.number="item.class_id"
                                                                                  value="6">
                                                                        Platinum
                                                                    </b-form-radio>
                                                                    <b-form-radio size="sm"
                                                                                  :name="'metals_selection'+index"
                                                                                  v-model.number="item.class_id"
                                                                                  value="7">
                                                                        Palladium
                                                                    </b-form-radio>
                                                                    <b-form-radio size="sm"
                                                                                  :name="'metals_selection'+index"
                                                                                  v-model.number="item.class_id"
                                                                                  value="3">Gold
                                                                        Filled
                                                                    </b-form-radio>
                                                                    <b-form-radio size="sm"
                                                                                  :name="'metals_selection'+index"
                                                                                  v-model.number="item.class_id"
                                                                                  value="11">Other
                                                                    </b-form-radio>
                                                                </b-form-radio-group>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Weight (dwt):"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              @click="$event.target.select()"
                                                                              v-model.number="item.tare_weight"
                                                                              :class="((item.tare_weight) < 0) ? 'input-warning' : null"
                                                                              min="0"
                                                                              @input="calculateItemComponentDWT(item)"
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Purity:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select
                                                                    id="input-purity" size="sm"
                                                                    v-model="item.purityPreset"
                                                                    :options="item.purities"
                                                                    v-on:input="setMixedMetalPurityValue(index, $event)"
                                                                >
                                                                </b-form-select>
                                                                <b-form-input size="sm" v-model.number="item.purity"
                                                                              type="number"
                                                                              :class="(item.purity === null || item.purity === '' || item.purity < 0 || (item.purity_type == 'kt' ? 24.01 : 100.01) < item.purity) ? 'input-warning' : null"
                                                                              v-if="item.purityPreset === 'other'">
                                                                </b-form-input>
                                                                <b-form-select v-model="item.purity_type"
                                                                               v-if="item.purityPreset === 'other'">
                                                                    <b-form-select-option v-if="isGoldType(item)"
                                                                                          value="kt">kt
                                                                    </b-form-select-option>
                                                                    <b-form-select-option v-if="!isGoldType(item)"
                                                                                          value="%">%
                                                                    </b-form-select-option>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                </b-list-group-item>
                                            </template>
                                            <!--End Mixed Metal-->
                                        </b-list-group>
                                    </b-collapse>
                                </b-card>

                                <!-- SUBTRACTIONS -->
                                <b-card no-body class="sub-add-accordion header-sm">
                                    <b-card-header header-tag="header" class="p-0" role="tab">
                                        <b-button block variant="light" href="#" v-b-toggle.subtractions
                                                  class="text-left">
                                            <span class="label">Weight Deductions</span>

                                            <b-button size="sm" pill class="ml-3 mr-1" @click.stop.prevent="addGem"><i
                                                class="fa-regular fa-plus"></i> Gem
                                            </b-button>
                                            <b-button size="sm" pill class="mr-1" @click.stop.prevent="addPackaging"><i
                                                class="fa-regular fa-plus"></i> Packaging
                                            </b-button>
                                            <b-button size="sm" pill class="mr-1"
                                                      @click.stop.prevent="addWatchMovement"><i class="fa-regular fa-plus"></i>
                                                Watch Movement
                                            </b-button>
                                            <!--
                                            <b-button size="sm" pill class="mr-1"
                                                      @click.stop.prevent="addSyntheticStone"><i
                                                class="fa-regular fa-plus"></i> Synthetic Stone
                                            </b-button> -->
                                            <b-button size="sm" pill class="mr-1" @click.stop.prevent="addPearls"><i
                                                class="fa-regular fa-plus"></i> Pearls
                                            </b-button>
                                            <b-button size="sm" pill class="mr-1" @click.stop.prevent="addOther"><i
                                                class="fa-regular fa-plus"></i> Other
                                            </b-button>
                                        </b-button>
                                    </b-card-header>
                                    <b-collapse id="subtractions" role="tabpanel" v-model="subtractionsVisible">
                                        <b-list-group flush>
                                            <!--Gem-->
                                            <template ref="gem"
                                                      v-for="(item, index) in itemDescription.itemComponents"
                                            >
                                                <b-list-group-item :key="index" v-if="isGem(item) && index > 0">
                                                    <b-row class="child">
                                                        <b-col>
                                                            <i class="fab fa-connectdevelop fa-lg"></i> Gem
                                                        </b-col>
                                                        <b-col class="text-right">
                                                            <b-button variant="link" size="sm" v-if="index > 0"
                                                                      @click="deleteItem(index)"><i
                                                                class="fa-light fa-trash-alt"></i></b-button>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                :label="item.weight_type == 'dwt' ? 'Weight (dwt):':
                                                                  item.weight_type == 'carats' ? 'Ct (ea):' :
                                                                  'Points (ea.):' "
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              @click="$event.target.select()"
                                                                              min="0"
                                                                              :step="item.weight_type == 'carats' ? .01 : 1"
                                                                              v-model.number="item.tare_weight"
                                                                              :class="((item.tare_weight) < 0) ? 'input-warning' : null"
                                                                              @change="calculateItemComponentDWT(item)"
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>

                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Qty:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              v-model.number="item.quantity"
                                                                              :class="((item.quantity) < 1) ? 'input-warning' : null"
                                                                              min="1"
                                                                              @click="$event.target.select()"
                                                                              @change="calculateItemComponentDWT(item)">
                                                                </b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Color:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="getGemColorByType(item.type_id)"
                                                                               v-model="item.color_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                </b-list-group-item>
                                                <b-list-group-item :key="index" v-if="isGem(item) && index == 0">
                                                    <b-row class="child">
                                                        <b-col>
                                                            <i class="fab fa-connectdevelop fa-lg"></i> Gem
                                                        </b-col>
                                                        <b-col class="text-right">
                                                            <b-button variant="link" size="sm" v-if="index > 0"
                                                                      @click="deleteItem(index)"><i
                                                                class="fa-light fa-trash-alt"></i></b-button>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                :label="item.weight_type == 'dwt' ? 'Weight (dwt):':
                                                                      item.weight_type == 'carats' ? 'Ct (ea):' :
                                                                      'Points (ea.):' "
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              @click="$event.target.select()"
                                                                              :step="item.weight_type == 'carats' ? .01 : 1"
                                                                              v-model.number="item.tare_weight"
                                                                              min="0"
                                                                              :class="((item.tare_weight) < 0) ? 'input-warning' : null"
                                                                              @change="calculateItemComponentDWT(item)"
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>

                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Qty:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              v-model.number="item.quantity"
                                                                              :class="((item.quantity) < 1) ? 'input-warning' : null"
                                                                              min="1"
                                                                              @click="$event.target.select()"
                                                                              @change="calculateItemComponentDWT(item)">
                                                                </b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>

                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Type:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="componentOptions.gem.types"
                                                                               v-model="item.type_id"
                                                                >
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Color:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="getGemColorByType(item.type_id)"
                                                                               v-model="item.color_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>

                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Cut:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="getGemStylesByType(item.type_id)"
                                                                               v-model="item.style_id"
                                                                >
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                </b-list-group-item>
                                            </template>
                                            <!--End Gem-->
                                            <!--Synthetic-->
                                            <template ref="gem"
                                                      v-for="(item, index) in itemDescription.itemComponents"
                                            >
                                                <b-list-group-item :key="index"
                                                                   v-if="isSyntheticStone(item) && index > 0">
                                                    <b-row class="child">
                                                        <b-col>
                                                            <i class="fab fa-connectdevelop fa-lg"></i> Synthetic Stone
                                                        </b-col>
                                                        <b-col class="text-right">
                                                            <b-button variant="link" size="sm"
                                                                      @click="deleteItem(index)" v-if="index > 0"><i
                                                                class="fa-light fa-trash-alt"></i></b-button>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                :label="item.weight_type == 'dwt' ? 'Weight (dwt):':
                                                                      item.weight_type == 'carats' ? 'Ct (ea):' :
                                                                      'Points (ea.):' "
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              @click="$event.target.select()"
                                                                              :step="item.weight_type == 'carats' ? .01 : 1"
                                                                              v-model.number="item.tare_weight"
                                                                              min="0"
                                                                              :class="((item.tare_weight) < 0) ? 'input-warning' : null"
                                                                              @change="calculateItemComponentDWT(item)"
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>

                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Qty:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              v-model.number="item.quantity"
                                                                              :class="((item.quantity) < 1) ? 'input-warning' : null"
                                                                              min="1"
                                                                              @click="$event.target.select()"
                                                                              @change="calculateItemComponentDWT(item)">
                                                                </b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Color:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="getGemColorByType(item.type_id)"
                                                                               v-model="item.color_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                </b-list-group-item>
                                                <b-list-group-item :key="index"
                                                                   v-if="isSyntheticStone(item) && index == 0">
                                                    <b-row class="child">
                                                        <b-col>
                                                            <i class="fab fa-connectdevelop fa-lg"></i> Synthetic Stone
                                                        </b-col>
                                                        <b-col class="text-right">
                                                            <b-button variant="link" size="sm"
                                                                      @click="deleteItem(index)" v-if="index > 0"><i
                                                                class="fa-light fa-trash-alt"></i></b-button>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                :label="item.weight_type == 'dwt' ? 'Weight (dwt):':
                                                                      item.weight_type == 'carats' ? 'Ct (ea):' :
                                                                      'Points (ea.):' "
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              @click="$event.target.select()"
                                                                              :step="item.weight_type == 'carats' ? .01 : 1"
                                                                              v-model.number="item.tare_weight"
                                                                              min="0"
                                                                              :class="((item.tare_weight) < 0) ? 'input-warning' : null"
                                                                              @change="calculateItemComponentDWT(item)"
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>

                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Qty:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              v-model.number="item.quantity"
                                                                              :class="((item.quantity) < 1) ? 'input-warning' : null"
                                                                              min="1"
                                                                              @click="$event.target.select()"
                                                                              @change="calculateItemComponentDWT(item)">
                                                                </b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>

                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Type:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="componentOptions.gem.types"
                                                                               v-model="item.type_id"
                                                                >
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Color:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="getSyntheticColorByType(item.type_id)"
                                                                               v-model="item.color_id"
                                                                >
                                                                    <template #first>
                                                                        <b-form-select-option
                                                                            :value="null"></b-form-select-option>
                                                                    </template>
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                        <b-col>

                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Cut:"
                                                                label-size="sm"
                                                            >
                                                                <b-form-select size="sm"
                                                                               :options="getSyntheticStylesByType(item.type_id)"
                                                                               v-model="item.style_id"
                                                                >
                                                                </b-form-select>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                </b-list-group-item>
                                            </template>
                                            <!--End Synthetic-->
                                            <!--Packaging-->
                                            <template ref="packaging"
                                                      v-for="(item, index) in itemDescription.itemComponents"
                                            >
                                                <b-list-group-item :key="index" v-if="isPackaging(item)">
                                                    <b-row class="child">
                                                        <b-col>
                                                            <i class="fa-regular fa-archive fa-lg"></i> Packaging
                                                        </b-col>
                                                        <b-col class="text-right">
                                                            <b-button variant="link" size="sm"
                                                                      @click="deleteItem(index)" v-if="index > 0"><i
                                                                class="fa-light fa-trash-alt"></i></b-button>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                    label-cols="4"
                                                                    label="Scale Weight (dwt):"
                                                                    label-size="sm"
                                                            >
                                                                <div class="">{{
                                                                    itemDescription.item.scale_dwt
                                                                    }} dwt
                                                                </div>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                min="0"
                                                                label="Weight (dwt):"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              @click="$event.target.select()"
                                                                              :title="((isCoin(itemDescription.item) && !coinWeightManual) || isMelee(itemDescription.item) || isDiamond(itemDescription.item)) ? '' : 'please enter a valid number'"
                                                                              :disabled="((isCoin(itemDescription.item) && !coinWeightManual) || isMelee(itemDescription.item) || isDiamond(itemDescription.item))"
                                                                              v-model.number="item.tare_weight"
                                                                              :class="((item.tare_weight) < 0) ? 'input-warning' : null"
                                                                              min="0"
                                                                              @change="calculateItemComponentDWT(item)"
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                    label-cols="4"
                                                                    :label="getClassName(itemDescription.itemComponents[0]) + ' (dwt):'"
                                                                    label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              :title="((isCoin(itemDescription.item) && !coinWeightManual) || isMelee(itemDescription.item) || isDiamond(itemDescription.item)) ? '' : 'please enter a valid number'"
                                                                              @click="$event.target.select()"
                                                                              :disabled="((isCoin(itemDescription.item) && !coinWeightManual) || isMelee(itemDescription.item) || isDiamond(itemDescription.item))"
                                                                              v-model.number="itemDescription.itemComponents[0].aggregate_dwt"
                                                                              @change="function(value) { calculateItemDifferenceChangePackage(itemDescription.itemComponents[0],item, value);}"
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                </b-list-group-item>
                                            </template>
                                            <!--End Packaging-->

                                            <!--Watch Movement-->
                                            <template ref="gem"
                                                      v-for="(item, index) in itemDescription.itemComponents"
                                            >
                                                <b-list-group-item :key="index" v-if="isWatchMovement(item)">
                                                    <b-row class="child">
                                                        <b-col>
                                                            <i class="fa-regular fa-clock fa-lg"></i> Watch Movement
                                                        </b-col>
                                                        <b-col class="text-right">
                                                            <b-button variant="link" size="sm"
                                                                      @click="deleteItem(index)" v-if="index > 0"><i
                                                                class="fa-light fa-trash-alt"></i></b-button>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Weight (dwt):"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              v-model.number="item.tare_weight"
                                                                              :class="((item.tare_weight) < 0) ? 'input-warning' : null"
                                                                              min="0"
                                                                              @click="$event.target.select()"
                                                                              @change="calculateItemComponentDWT(item)"
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                </b-list-group-item>
                                            </template>
                                            <!--End Watch Movement-->

                                            <!--Pearls-->
                                            <template ref="gem"
                                                      v-for="(item, index) in itemDescription.itemComponents"
                                            >
                                                <b-list-group-item :key="index" v-if="isPearls(item)">
                                                    <b-row class="child">
                                                        <b-col>
                                                            <i class="fa-regular fa-spinner fa-lg"></i> Pearls
                                                        </b-col>
                                                        <b-col class="text-right">
                                                            <b-button variant="link" size="sm"
                                                                      @click="deleteItem(index)" v-if="index > 0"><i
                                                                class="fa-light fa-trash-alt"></i></b-button>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <div class="text-center">Scale Weight -</div>
                                                            <div class="text-center">{{
                                                                    itemDescription.item.scale_dwt
                                                                }} dwt
                                                            </div>
                                                        </b-col>
                                                        <b-col>
                                                            <div class="text-center">Pearls =</div>
                                                            <div class="text-center">{{ item.tare_weight }} dwt</div>
                                                        </b-col>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Clasp (dwt):"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              @click="$event.target.select()"
                                                                              v-model.number="item.clasp_dwt"
                                                                              @input="calculateItemComponentDWT(item)"
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                </b-list-group-item>
                                            </template>
                                            <!--End Pearls-->

                                            <!--Other-->
                                            <template ref="gem"
                                                      v-for="(item, index) in itemDescription.itemComponents"
                                            >
                                                <b-list-group-item :key="index" v-if="isOther(item) && index > 0">
                                                    <b-row class="child">
                                                        <b-col>
                                                            <i class="fa-regular fa-trash fa-lg"></i> Other
                                                        </b-col>
                                                        <b-col class="text-right">
                                                            <b-button variant="link" size="sm" v-if="index > 0"
                                                                      @click="deleteItem(index)"><i
                                                                class="fa-light fa-trash-alt"></i></b-button>
                                                        </b-col>
                                                    </b-row>
                                                    <b-row>
                                                        <b-col>
                                                            <b-form-group
                                                                label-cols="4"
                                                                label="Weight (dwt):"
                                                                label-size="sm"
                                                            >
                                                                <b-form-input type="number" size="sm"
                                                                              @click="$event.target.select()"
                                                                              :class="((item.tare_weight) < 0) ? 'input-warning' : null"
                                                                              v-model.number="item.tare_weight"
                                                                              min="0"
                                                                              @change="calculateItemComponentDWT(item)"
                                                                ></b-form-input>
                                                            </b-form-group>
                                                        </b-col>
                                                    </b-row>
                                                </b-list-group-item>
                                            </template>
                                            <!--End Other-->
                                        </b-list-group>
                                    </b-collapse>
                                </b-card>
                            </div>
                        </template>


                        <!-- DETAILS -->
                        <b-card class="my-3" header-class="font-weight-bold" header="Details">
                            <b-row>
<!--                                <b-col>-->
<!--                                    <b-form-row>-->
<!--                                        <b-col>-->
<!--                                            <label for="appraiser-certificate" size="sm" class="font-weight-bold label">Certificate/<br>Appraisal:</label>-->
<!--                                            <b-form-checkbox-->
<!--                                                    id="appraiser-certificate"-->
<!--                                                    v-model="_certificate"-->
<!--                                                    name="check-button"-->
<!--                                                    class="mt-0"-->
<!--                                                    switch>-->
<!--                                            </b-form-checkbox>-->
<!--                                        </b-col>-->
<!--                                    </b-form-row>-->
<!--                                </b-col>-->
                                <b-col>
                                    <b-form-row>
                                        <b-col class="text-right">
                                            <label for="appraiser-notes" size="sm" class="font-weight-bold label">Appraisal Notes:</label>
                                        </b-col>
                                        <b-col cols="8">
                                            <b-form-textarea
                                                    id="appraiser-notes"
                                                    v-model="itemDescription.item.appraiser_notes"
                                                    @click="$event.target.select()"
                                                    size="md"
                                                    rows="2"
                                            ></b-form-textarea>
                                        </b-col>
                                    </b-form-row>
                                </b-col>
                            </b-row>
                        </b-card>

                        <!-- ACTION BUTTONS -->
                        <b-form-row>
                            <b-col>
                                <b-button size="lg" block @click="resetItem" class="font-weight-bold cancel-button" variant="danger">
                                    <i class="fa-regular fa-times-circle mr-1"></i>Cancel
                                </b-button>
                            </b-col>
                            <b-col>
                                <b-button size="lg" block variant="primary" class="text-white font-weight-bold"
                                          @click="saveItem"
                                          :disabled="!_canSave">
                                    <i class="text-white fa-regular fa-check-circle mr-1"></i><span
                                    v-if="$route.params.item_id">Update</span><span v-else>Save</span>
                                </b-button>
                            </b-col>
                            <b-col cols="6" v-if="from == 'QA'">
                                <b-button @click="saveItemQA()" :disabled="!_canSave" variant="success" block size="lg"><i
                                    class="fa-solid fa-file-check mr-2"></i>Save &amp; Return to QA
                                </b-button>
                            </b-col>
                            <b-col cols="6" v-else-if="from == 'Kits'">
                                <b-button @click="saveItemKits()" :disabled="!_canSave" variant="success" block size="lg"><i
                                    class="fa-solid fa-file-check mr-2"></i>Save &amp; Return to Kits View
                                </b-button>
                            </b-col>
                            <b-col cols="6" v-else-if="from == 'OfferGen'">
                                <b-button @click="saveItemOfferGen()" variant="success" block size="lg"
                                          :disabled="!_canSave"><i class="fa-solid fa-file-check mr-2"></i>Save &amp; Return
                                    to Offer Generation
                                </b-button>
                            </b-col>
                            <b-col cols="6" v-else>
                                <b-button size="lg" block variant="success" @click="saveItemAndAddNew"
                                          :disabled="!_canSave"
                                          class="font-weight-bold shadow">
                                    <i class="fa-regular fa-plus-circle mr-1"></i><span
                                    v-if="$route.params.item_id">Update</span><span v-else>Save</span> and Add New
                                </b-button>
                            </b-col>
                        </b-form-row>

                        <b-form-row class="text-center mt-2" v-if="saving">
                            <b-col>
                                <i class="fa-duotone fa-spinner fa-spin fa-lg fa-fw"></i> {{ savingStatus }}
                            </b-col>
                        </b-form-row>
                    </b-col>
                </b-row>
                <!--end START INVENTORY-->
            </template>

            <template v-else>
                <b-button :disabled="!isOrderProcessingInventory" variant="success" block size="lg" @click="addNewItem"
                          class="mb-3">
                    <i class="fa-solid fa-plus fa-fw"></i> Add New Item
                </b-button>

                <b-button :to="'/appraisals/bagging/'+$route.params.id" variant="primary" block size="lg"><i
                    class="fa-solid fa-box-circle-check mr-1"></i>Open Bagging
                    Summary
                </b-button>
                <b-button class="mt-3" v-if="from == 'QA'" :to="'/quality-assurance/view/'+$route.params.id"
                          variant="danger" block size="lg"><i class="fa-solid fa-file-check mr-2"></i>Return to QA
                </b-button>
                <b-button class="mt-3" v-if="from == 'Kits'" :to="'/kit/view/'+$route.params.id" variant="danger" block
                          size="lg"><i class="fa-solid fa-file-check mr-2"></i>Return to Kits View
                </b-button>
                <b-button class="mt-3" v-if="from == 'OfferGen'" :to="'/offer-generation/view/'+$route.params.id"
                          variant="danger" block size="lg"><i class="fa-solid fa-file-check mr-2"></i>Return to Offer
                    Generation
                </b-button>
            </template>

        </b-card>
        <NotesPopup :order_id="!order ? null : order.id" :customer_id="!order ? null : order.customer_id"
                    style="z-index: 1000">
        </NotesPopup>

        <!-- Saving Modal-->
        <b-modal id="modal-inventory-saving" centered hide-footer hide-header-close no-close-on-esc no-close-on-backdrop title="Saving..." v-model="saving">
            <i class="fa-duotone fa-spinner fa-spin fa-lg fa-fw"></i> {{ savingStatus }}
            <b-progress :value="savingProgress" :max="savingStepCount" variant="success" striped animated class="mt-2"></b-progress>
        </b-modal>
    </div>
</template>

<script>
import LoadingSpinner from '@/components/LoadingSpinner';
import VideoStreamerControl from '@/plugins/video-streamer/components/video-streamer-control';
import ScaleWeight from '@/components/ScaleWeight';
import Multiselect from 'vue-multiselect';
import NotesPopup from '../../components/NotesPopup';
import OrderCardHeader from '@/components/OrderCardHeader';
import PhotoWidget from '../../components/PhotoWidget';
import GenericPopups from '../../components/GenericPopups';
import WeightValidation from '../../components/WeightValidation';
import _ from 'lodash';

export default {
    name: 'appraisals-appraise',
    components: {
        ScaleWeight,
        VideoStreamerControl,
        LoadingSpinner,
        Multiselect,
        NotesPopup,
        OrderCardHeader,
        PhotoWidget: PhotoWidget,
        GenericPopups: GenericPopups,
        WeightValidation: WeightValidation,
    },
    data() {
        return {
            nextImageSaveIndex: null,
            runEAV: [],
            coinWatcher: null,
            selectedCoin: null,
            coinDropDownSelection: null,
            showCoinPopover: false,
            coinDropDownOpen: false,
            coinWeightManual: false,
            loading: false,
            working: false,
            saving: false,
            from: null,
            savingStatus: null,
            savingStepCount: null,
            savingProgress: null,
            activeItem: false,
            scaleStable: false,
            scaleWeight: null,
            scaleSaved: false,
            scaleVarianceError: false,
            number: 3,
            customer: null,
            order: null,
            current_inventory_weight: null,
            total_inventory_weight: null,
            additionVisible: false,
            subtractionsVisible: false,
            itemOptions: [],
            itemClasses: [],
            itemPurityPreset: null,
            itemSizePreset: null,
            bagOptions: [
                {value: null, text: 'Please select an option', disabled: true,},
                {value: 1, text: 'Small Bag'},
                {value: 2, text: 'Medium Bag'},
                {value: 3, text: 'Large Bag'},
                {value: 4, text: 'X-Large Bag'}
            ],
            sizeOptionsByItemTypeId: {
                1: [
                    7,
                    8,
                    9,
                    10,
                    12,
                ],
                9: [
                    12,
                    14,
                    16,
                    18,
                    20,
                    22,
                    24,
                    26,
                    28,
                    30,
                    32,
                    36,
                    38,
                ],
                12: [
                    3,
                    3.25,
                    3.5,
                    3.75,
                    4,
                    4.25,
                    4.5,
                    4.75,
                    5,
                    5.25,
                    5.5,
                    5.75,
                    6,
                    6.25,
                    6.5,
                    6.75,
                    7,
                    7.25,
                    7.5,
                    7.75,
                    8,
                    8.25,
                    8.5,
                    8.75,
                    9,
                    9.25,
                    9.5,
                    9.75,
                    10,
                    10.25,
                    10.5,
                    10.75,
                    11,
                    11.25,
                    11.5,
                    11.75,
                    12,
                    12.25,
                    12.5,
                    12.75,
                    13,
                    13.25,
                    13.5,
                    13.75,
                    14,
                ],
            },
            hold_bag_id: 2,
            itemDescription: {
                item:
                    initialData()
                ,
                itemComponentTemplate: {
                    class_id: null,
                    purity: null,
                    purity_type: 'kt',
                    category_id: null,
                    type_id: null,
                    style: null,
                    style_id: null,
                    brand_id: null,
                    certificate_number: null,
                    certificate_type_id: null,
                    size: null,
                    clarity_id: null,
                    color_id: null,
                    condition_id: null,
                    quantity: 1,
                    tare_weight: 0,
                    net_dwt: 0,
                    aggregate_dwt: 0,
                    weight_type: 'dwt',
                    numismatic_value: null,
                },
                itemComponents: [
                    {},
                ],
                oldItemComponents: [
                    {}
                ],
                options: {
                    brands: [],
                    certificates: [],
                    colors: [],
                    conditions: [],
                    purities: [],
                    styles: [],
                },
                pictures: [],
                deletedPictureUrls: [],
            },
            originalItemDescription: null,
            componentOptions: {
                boxAndPaperOptions: ['none', 'box', 'papers', 'both'],
                melee: {
                    colors: [],
                    clarities: [],
                    styles: [],
                },
                diamond: {
                    colors: [],
                    clarities: [],
                    styles: [],
                    types: [],
                    certificates: []
                },
                precious_stone: {
                    types: [],
                    colors: [],
                    styles: [],
                },
                gem: {
                    types: [],
                    colors: [],
                    styles: [],
                },
            }
        };
    },
    created() {
        document.addEventListener('click', this.closeCoinPopover);
        this.getData();
        this.$parent.getInventory(this.$route.params.id);
//        window.onbeforeunload = function () {
//            document.querySelector('.container-fluid').scrollTo(0, 0);
//        }
    },
    computed: {
        _manualMode: {
            get: function() {
                return this.coinWeightManual;
            },
            set: function(value) {
                this.toggleCoinManual(value);
            }
        },
        isOrderProcessingInventory() {
            if (this.order) {
                switch (this.order.status_name) {
                    case 'Kit Requested':
                    case 'Kit Shipped':
                    case 'Kit Inbound':
                    case 'Kit Received':
                    case 'Appraisal':
                    case 'Appraisal Hold':
                        return true;
                }
            }
            return false;
        },
        preventEAVEdit() {
            if (this.order) {
                switch (this.order.status_name) {
                    case 'Kit Requested':
                    case 'Kit Shipped':
                    case 'Kit Inbound':
                    case 'Kit Received':
                    case 'Appraisal':
                        return true;
                }
            }
            return false;
        },
        _orderTotalWeight() {
            let ret = this.itemDescription.item.scale_dwt || 0;
            _.forEach(this.$parent.inventoryListSummary, (item) => {
                if (item.id != this.itemDescription.item.id) {
                    ret += item.aggregate_dwt;
                }
            });
            return ret;
        },
        _packagesTotalWeight() {
            return this.order.inventory_package_weight;
        },
        _totalEAV: function () {
            var eav = parseFloat(this.itemDescription.item.numismatic_value) || 0;
            for (var i = 0; i < this.itemDescription.itemComponents.length; i++) {
                eav += parseFloat(this.itemDescription.itemComponents[i].numismatic_value) || 0;
            }
            return eav.toFixed(2);
        },
        _isMetal: function () {
            if (this.itemDescription.item.selected_type !== null) {
                let category_id = this.itemDescription.item.selected_type.item_category_id;
                return category_id !== 7 && category_id !== 8 && category_id !== 9 && category_id !== 12 && category_id !== 13;
            }
            return false;
        },
        _isGoldType: function () {
            //for parent it
            if (this.itemDescription.itemComponents[0]) {
                return this.isGoldType(this.itemDescription.itemComponents[0]);
            }
            return false;
        },
        _itemClass: function () {
            if (this.itemDescription.itemComponents[0] && this.itemDescription.itemComponents[0].class_id) {
                return this.itemDescription.itemComponents[0].class_id;
            } else {
                return null;
            }
        },
        _itemPurity: {
            get: function () {
                if (this.itemPurityPreset === 'other') {
                    return 'other';
                }
                if (this._itemClass) {
                    let item_class = _.find(this.itemClasses, ['id', this._itemClass]);
                    if (item_class) { //fixes bug of switching metal types.
                        let purity_object = _.find(item_class.purities, ['purity', _.round(this.itemDescription.itemComponents[0].purity,2)]);
                        return purity_object ? purity_object.id : null;
                    }
                } 
                
                return null;
            },
            set: function (value) {
                this.itemPurityPreset = value;
                if (value !== 'other' && value !== null) {
                    let item_class = _.find(this.itemClasses, ['id', this._itemClass]);
                    if(item_class) {
                        let purity_object = _.find(item_class.purities, ['id', value]);
                        if(purity_object) {
                            this.itemDescription.itemComponents[0].purity = purity_object.purity;
                            this.itemDescription.itemComponents[0].purity_type = purity_object.purity_type;
                        } else {
                            this.itemDescription.itemComponents[0].purity = null;
                            this.itemDescription.itemComponents[0].purity_type = null;
                        }
                    } else {
                        this.itemDescription.itemComponents[0].purity = null;
                        this.itemDescription.itemComponents[0].purity_type = null;
                    }
                } else if (value === null) {
                    this.itemDescription.itemComponents[0].purity = null;
                    this.itemDescription.itemComponents[0].purity_type = null;
                }
            }
        },
        _itemSize: {
            get: function () {
                if (this.itemSizePreset === 'other') {
                    return 'other';
                } else {
                    return this.itemDescription.item.size;
                }
            },
            set: function (value) {
                this.itemSizePreset = value;
                if (this.itemSizePreset !== 'other') {
                    this.itemDescription.item.size = value;
                }
            }
        },
        _certificate: {
            get: function () {
                if (!this.itemDescription.item.certificate_type_id || this.itemDescription.item.certificate_type_id === 7) {
                    return false;
                } else {
                    return true;
                }
            },
            set: function (val) {
                if (val) {
                    if (!this.itemDescription.item.certificate_type_id || this.itemDescription.item.certificate_type_id === 7) {
                        this.itemDescription.item.certificate_type_id = 6;
                    }
                } else {
                    this.itemDescription.item.certificate_type_id = null;
                }
            }
        },
        _canChildrenSave() {
            if (this._canEdit === false) {
                return false;
            }
            for (let i = 0; i < this.itemDescription.itemComponents.length; i++) {
                let item = this.itemDescription.itemComponents[i];
                if (!item.class_id || !item.category_id || item.aggregate_dwt < 0 || ((this.isMetal(item) || this.isCoin(item)) && (((item.purity === '' || item.purity === null) && item.class_id !== 11) || (item.purity_type == 'kt' ? 24.01 : 100.01) < item.purity || item.purity < 0 || !item.purity_type))) {
                    return false;
                }
                if (item.tare_weight < 0) {
                    return false;
                }
                if (item.quantity < 1) {
                    return false;
                }
                if (i == 0 && (this.isDiamond(item) || this.isMelee(item))) {
                    if (!item.quantity || !item.color_id || !item.clarity_id || !item.style_id) {
                        return false;
                    }
                }
            }
            return true;
        },
        _canSave: function () {
            return !this.working
                && this.scaleSaved
                && this.itemDescription.item.type_id
                && this._canChildrenSave
                && (this.itemDescription.itemComponents[0].color_id || this._disabledColor)
                && this.itemDescription.item.quantity
                && this.itemDescription.item.quantity >= 1
                && (!this._displayStyle || this.itemDescription.itemComponents[0].style_id)
                && (!this._displayBrand || this.itemDescription.item.selected_type.item_category_id != 3 || (this.itemDescription.item.brand_id && this.itemDescription.item.selected_type.item_category_id == 3))
                && this.itemDescription.pictures.length != 0;
        },
        _displayConditionNonCore: function () {
            if (this.itemDescription != null) {
                switch (this.itemDescription.item.type_id) { //todo move 'should Engraving' attributes to Database, probably on item types record.
                }
                switch (this.itemDescription.item.category_id) {
                    case 13: //diamond
                        return true;
                }
            }
            return false;
        },
        _displayEngravings: function () {

            if (this.itemDescription != null) {
                switch (this.itemDescription.item.type_id) { //todo move 'should Engraving' attributes to Database, probably on item types record.
                    case 5://Metal
                    case 10://other- jewelery
                        return false;
                }
                switch (this.itemDescription.item.category_id) {
                    //moved to child level only
                    //case 7: //precious stone
                    //case 8: //synthetic stone
                    //case 9: //gemstone
                    case 4: //dental
                    case 13: //diamond
                        return false;
                }
            }
            return true;
        },
        _displayStyle: function () {
            if (this.itemDescription != null) {
                switch (this.itemDescription.item.type_id) { //todo move 'should display' attributes to Database, probably on item types record.
                    //brooch, cufflinks, charm, charm holder, money clip, tie clip, wire, the crucible, flatware, other precious metal
                    case 2: //brooch
                    case 3: //cuff links
                    case 4: //charm
                    case 5: //charm holder
                    case 8: //money clip
                    case 10://other- jewelery
                    case 13: //tie clip
                    case 28: //other precious metal
                    case 29: //wire
                    case 31: //cruicible
                    case 32: //other- indsturial metal
                    case 53: //pin type
                        return false;
                }
                switch (this.itemDescription.item.category_id) {
                    case 3: //watch
                    case 4: //dental
                    case 5: //Metal
                    case 6: //industrial metal
                    case 7://precious stone
                    case 8: //synthetic stone
                    case 9: //gem-stone
                    case 10: //flatware
                    case 11: // other unknowns (packages, other unknown)
                    case 13://diamond
                        return false;
                }
            }
            return true;
        },
        _displayBrand: function () {
            if (this.itemDescription != null) {
                //Remove "brand field" from the following item types - charm, Charm holder, precious stone, synthetic stones, industrial (everything in this section), gemstones, Other precious meta
                switch (this.itemDescription.item.type_id) { //todo move 'should display' attributes to Database, probably on item types record.
                    // case 4: //charm
                    case 5: //char holder
                    case 10: //other - jewelery
                    case 28: //other precious metal
                        return false;
                }
                switch (this.itemDescription.item.category_id) {
                    case 4: //dental
                    case 5: //metal
                    case 6: //industrial
                    case 7: //precious stone
                    case 8: //synthetic stone
                    case 9: //gemstone
                    case 11: //other unknowns
                        //case 13: //diamond
                        return false;
                }
            }
            return true;
        },
        _display_size: function () {
            return this.itemDescription.item.selected_type != null && this.itemDescription.item.selected_type.id == 12;
        },
        _display_length: function () {
            return this.itemDescription.item.selected_type != null && (this.itemDescription.item.selected_type.id == 9 || this.itemDescription.item.selected_type.id == 1);
        },
        _disabledColor: function () {
            if (this.itemDescription != null) {
                switch (this.itemDescription.itemComponents[0].type_id) {
                    case 51: //pearl
                        return true;
                }
            }
            return false;
        },
        _displayReferenceNumber: function () {
            return this.itemDescription.item.category_id == 3 || this.itemDescription.item.category_id == 9;
        },
        _displayBoxAndPapers: function () {
            return this.itemDescription.item.category_id == 3;
        },
        _itemTypeCategoryName: function () {
            if (this.itemDescription.item.category_id) {
                return _.find(this.itemOptions, ['id', this.itemDescription.item.category_id]).name;
            } else {
                return false;
            }
        },
        _canEdit() {
            return !(this.order.status_id === 8
                || this.order.status_id === 9
                || this.order.status_id === 10
                || this.order.status_id === 11
                || this.order.status_id === 12
                || this.order.status_id === 13
                || this.order.status_id === 14
                || this.order.status_id === 15);
        },
        _sortedCoins() {
            return _.sortBy(this.itemDescription.options.styles, ['rank']);
        },
        _orderCardHeaderBGColor() {
            // Check for flag type 57 (Returning Items)
            let f = _.find(this.order.flags, ['flag_type_id', 57]);
            if (f && f.cleared_at == null) { 
                return 'warning-light';
            }
            return '';
        },
    },

    methods: {
        getData() {
            var _this = this;
            this.loading = true;
            this.working = true;
            this.scaleStable = false;
            this.scaleWeight = null;
            this.scaleSaving = false;
            this.scaleSaved = false;
            this.order = null;
            this.packages = null;

            // Get item options
            this.$store.dispatch('GET_REQUEST', '/items/categories')
                .then(response => {
                    this.itemOptions = response.data.data;
                    _.forEach(response.data.data, (value) => {
                        switch (value.name) {
                            case 'Diamond':
                                // Set Melee options
                                this.componentOptions.melee.colors = _.map(value.colors, (value) => {
                                    return {
                                        value: value.id,
                                        text: value.name
                                    };
                                });
                                this.componentOptions.melee.clarities = _.map(_this.sortByOrder(value.clarities), (value) => {
                                    return {
                                        value: value.id,
                                        text: value.name
                                    };
                                });
                                this.componentOptions.melee.styles = _.map(_this.sortAlphabetically(_.find(value.types, ['name', 'Melee']).styles, 'name'), (value) => {
                                    return {
                                        value: value.id,
                                        text: value.name
                                    };
                                });

                                // Set Diamond options
                                this.componentOptions.diamond.colors = _.map(value.colors, (value) => {
                                    return {
                                        value: value.id,
                                        text: value.name
                                    };
                                });
                                this.componentOptions.diamond.clarities = _.map(_this.sortByOrder(value.clarities), (value) => {
                                    return {
                                        value: value.id,
                                        text: value.name
                                    };
                                });
                                this.componentOptions.diamond.styles = _.map(_this.sortAlphabetically(_.find(value.types, ['name', 'Diamond']).styles, 'name'), (value) => {
                                    return {
                                        value: value.id,
                                        text: value.name
                                    };
                                });
                                this.componentOptions.diamond.certificates = _.map(_this.sortAlphabetically(value.certificates, 'name'), (value) => {
                                    return {
                                        value: value.id,
                                        text: value.name
                                    };
                                });
                                break;
                            case 'Precious Stone':
                                // Set Precious Stone options
                                this.componentOptions.precious_stone.types = _.map(_this.sortAlphabetically(value.types, 'name'), (value) => {
                                    return {
                                        value: value.id,
                                        text: value.name
                                    };
                                });


                                // Set Diamond options
                                this.componentOptions.precious_stone.colors = _.map(value.colors, (value) => {
                                    return {
                                        value: value.id,
                                        text: value.name
                                    };
                                });
                                break;

                            case 'Gem Stone':
                                // Set Gem Stone options
                                this.componentOptions.gem.types = _.map(_this.sortAlphabetically(value.types, 'name'), (value) => {
                                    return {
                                        value: value.id,
                                        text: value.name
                                    };
                                });
                                break;
                        }
                    });
                    this.$store.dispatch('GET_REQUEST', '/items/classes')
                        .then(response => {
                            this.itemClasses = response.data.data;


                            this.$http.get('/orders/' + this.$route.params.id + '?include=packages')
                                .then(response => {
                                    let data = response.data.data;
                                    var totalWeight = 0;
                                    _.forEach(data.packages, (pack) => {
                                        //any package is not proper set
                                        if (pack.items_dwt == null) {
                                            this.$router.push('/appraisals/process/' + data.id);
                                            return;
                                        }
                                        totalWeight += parseFloat(pack.items_dwt);
                                    });
                                    this.order = {
                                        id: data.id,
                                        customer: data.customer,
                                        customer_id: data.customer.id,
                                        full_name: data.customer.full_name,
                                        inventory_package_weight: totalWeight,
                                        status_id: data.status_id,
                                        status_name: data.status_name,
                                        flags: data.flags,
                                        appraised_at: data.appraised_at,
                                        processor_name: data.processor_name,
                                    };
                                    //is after received
                                    if (this.order.status_id < 5) {
                                        this.$router.push('/appraisals/process/' + this.order.id);
                                    }
                                    this.$store.commit('setActiveOrder', this.order);
                                    // Create base component
                                    this.createBaseItem();
                                    if (this.$route.params.item_id) {
                                        this.$http.get('/inventory/' + this.$route.params.item_id).then((response) => {
                                            let data = response.data.data;
                                            console.log('loaded', data);
                                            let children = data.children;
                                            if (data.category_id == 2 || data.category_id == 13) {
                                                for (let i = 0; i < data.children.length; i++) {
                                                    let child = data.children[i];
                                                    console.log('category_ids', child);
                                                    if (child.class_id == 11 && child.category_id == 11 && child.type_id == 49) {
                                                        this.itemDescription.coin_packaging_id = i;
                                                        break;
                                                    }
                                                }
                                                let style_id = parseInt(children[0].style_id);
                                                let qty = children[0].quantity;
                                                let style = children[0].style;
                                                // _.find(children[0].type.styles, ['id', style_id]);
                                                if(style) {
                                                    this.coinDropDownSelection = {
                                                        value: style.id,
                                                        text: style.name
                                                    };
                                                    if(style.coin_definition) {
                                                        let coin = style.coin_definition;
                                                        let comparedWeight = _.round(this.convertTroyOuncesToDWT(coin.troy_ounce * qty), 2);
                                                        //TODO proper detection for true mixed metal coin, not supported currently by backend architecture.
                                                        //either its a child 0 w/ no packaging since its perfect weight or 2 children, 1 base with correct weight and 1 packaging addon.
                                                        if (data.children.length == 1 || (data.children.length == 2 && data.children[1].type_id == 49)) {
                                                            this.coinWeightManual = (Math.abs(data.children[0].aggregate_dwt - comparedWeight) > .01);
                                                        } else {
                                                            //manual input of coin other children
                                                            this.coinWeightManual = true;
                                                        }
                                                    } else {
                                                        //Other coin probably
                                                        this.coinWeightManual = true;
                                                    }
                                                }
                                                else {
                                                    //unusual input of coin, style not detected
                                                    this.coinWeightManual = true;
                                                }
                                            }
                                            this.scaleSaved = true; //allows resaving without weighing it.
                                            this.itemDescription.item = {
                                                id: parseInt(data.id),
                                                category_id: parseInt(data.category_id),
                                                type_id: parseInt(data.children[0].type.id),
                                                selected_type: _.find(_.find(this.getItemOptions(),(e) => {console.log(e); return e.id == data.children[0].category_id;}).types, (e) => {return e.id == data.children[0].type_id}),
                                                brand_id: parseInt(data.brand_id),
                                                class_id: parseInt(data.children[0].class_id),
                                                selected_brand: data.children[0].brand,
                                                model_number: data.model_number,
                                                serial_number: data.serial_number,
                                                certificate_number: data.certificate_number,
                                                certificate_type_id: data.certificate_type_id,
                                                selected_certificate: data.selected_certificate,
                                                size: data.size,
                                                reference_number: data.reference_number,
                                                box_and_papers: data.box_and_papers,
                                                condition_id: parseInt(data.condition_id),
                                                selected_condition: data.children[0].condition,
                                                quantity: data.quantity,
                                                notes: data.notes,
                                                scale_dwt: 0,//data.scale_dwt,
                                                aggregate_dwt: data.children[0].aggregate_dwt,
                                                inventory_status_id: data.inventory_status_id,
                                                additional_packaging: data.additional_packaging ? true : false,
                                                appraiser_notes: data.appraiser_notes,
                                                numismatic_value: data.numismatic_value,
                                                verificationMethods: {
                                                    xrf: data.verification_xrf ? true : false,
                                                    acid: data.verification_acid ? true : false,
                                                    magnet: data.verification_magnet ? true : false,
                                                    hallmark: data.verification_hallmark ? true : false,
                                                    other: data.verification_other ? true : false,
                                                }
                                            };
                                            if(data.inventory_status_id == 2 && data.bag) {
                                                this.hold_bag_id = parseInt(data.bag.bag_type_id);
                                            } else {
                                                this.hold_bag_id = 2;
                                            }
                                            this.itemDescription.itemComponents = [];
                                            let index = 0;
                                            _.forEach(children, (i) => {
                                                this.itemDescription.item.scale_dwt += parseFloat(i.aggregate_dwt);
                                                //adding can have weird floating point errors
                                                this.itemDescription.item.scale_dwt = _.round(this.itemDescription.item.scale_dwt, 2);
                                                this.itemDescription.itemComponents.push({
                                                    'component_id': parseInt(i.component_id),
                                                    'class_id': parseInt(i.class_id),
                                                    'purity': i.purity,
                                                    'purity_type': i.purity_type,
                                                    'category_id': parseInt(i.category_id),
                                                    'type_id': parseInt(i.type_id),
                                                    'style': i.style,
                                                    'style_id': parseInt(i.style_id),
                                                    'brand_id': parseInt(i.brand_id),
                                                    'model_number': i.model_number,
                                                    'serial_number': i.serial_number,
                                                    'certificate_number': i.certificate_number,
                                                    'certificate_type_id': i.certificate_type_id,
                                                    'reference_number': i.reference_number,
                                                    'box_and_papers': i.box_and_papers,
                                                    'size': i.size,
                                                    'clarity_id': parseInt(i.clarity_id),
                                                    'color_id': parseInt(i.color_id),
                                                    'condition_id': parseInt(i.condition_id),
                                                    'quantity': i.quantity,
                                                    'notes': i.notes,
                                                    'tare_weight': i.tare_weight,
                                                    'weight_type': i.weight_type,
                                                    'net_dwt': i.net_dwt,
                                                    'aggregate_dwt': i.aggregate_dwt,
                                                    'numismatic_value': i.numismatic_value,
                                                });
                                                this.setMixedMetalPurityOptions(index, i.class_id);
                                                index++;
                                            });
                                            this.itemDescription.pictures = data.media;
                                            if (this.itemDescription.itemComponents.length > 1) {
                                                this.additionVisible = true;
                                                this.subtractionsVisible = true;
                                            }
                                            this.$nextTick(() => {
                                                //loading needs to be very last due to process
                                                this.working = false;
                                                this.activeItem = true;
                                                let childWeightCount = 0;
                                                let pearl = {};
                                                if (this.$route.query.after_save) {
                                                    this.from = this.$route.query.after_save;
                                                }
                                                //FIXME probably a better way to fix this bug, but the watcher unsets inventory_status_id becasue type was set.
                                                this.itemDescription.item.inventory_status_id = data.inventory_status_id;
                                                //FIXME correcting issue by setting metal type (again watcher)
                                                let index = 0;
                                                _.forEach(children, (i) => {
                                                    this.itemDescription.itemComponents[index].purity = i.purity ? (i.purity_type == "kt" ? parseInt(i.purity) : _.round(i.purity,2)) : null;
                                                    this.itemDescription.itemComponents[index].purity_type = i.purity_type;
                                                    this.itemDescription.itemComponents[index].dwt = i.dwt;
                                                    // Calculate DWT weights
                                                    if (i.weight_type === 'points') {
                                                        this.itemDescription.itemComponents[index].net_dwt = this.convertPointsToDWT(i.tare_weight);
                                                        this.itemDescription.itemComponents[index].aggregate_dwt = _.round(this.convertPointsToDWT(i.tare_weight * i.quantity), 2);
                                                    } else if (i.weight_type === 'carats') {
                                                        this.itemDescription.itemComponents[index].net_dwt = this.convertCaratsToDWT(i.tare_weight);
                                                        this.itemDescription.itemComponents[index].aggregate_dwt = _.round(this.convertCaratsToDWT(i.tare_weight * i.quantity), 2);
                                                    } else {
                                                        this.itemDescription.itemComponents[index].net_dwt = _.round(i.tare_weight, 2);
                                                        this.itemDescription.itemComponents[index].aggregate_dwt = _.round(i.tare_weight, 2);
                                                    }
                                                    if (this.isDiamond(i) || this.isMelee(i)) {
                                                        this.itemDescription.itemComponents[index].tare_weight = i.tare_weight;
                                                    }
                                                    this.itemDescription.itemComponents[index].tare_weight;
                                                    if (this.itemDescription.itemComponents[index] && this.itemDescription.itemComponents[index].class_id) {
                                                        let class_id = this.itemDescription.itemComponents[index].class_id;
                                                        let item_class = _.find(this.itemClasses, ['id', class_id]);
                                                        let purity_object = _.find(item_class.purities, ['purity', (_.round(this.itemDescription.itemComponents[index].purity,2))]);
                                                        if (purity_object) {
                                                            if (index == 0) {
                                                                this.itemPurityPreset = purity_object.id;
                                                            }
                                                            this.itemDescription.itemComponents[index].purityPreset = purity_object.id;
                                                        } else {
                                                            if (index == 0) {
                                                                this.itemPurityPreset = 'other';
                                                            }
                                                            this.itemDescription.itemComponents[index].purityPreset = 'other';
                                                        }
                                                    } else if (this.itemDescription.itemComponents[index]) {
                                                        this.itemDescription.itemComponents[index].purityPreset = 'other';
                                                    }
                                                    if (this.itemDescription.itemComponents[index].type_id != 51) {
                                                        if (index != 0) {
                                                            childWeightCount += this.itemDescription.itemComponents[index].aggregate_dwt;
                                                        }
                                                    } else {
                                                        pearl = this.itemDescription.itemComponents[index];
                                                    }
                                                    index++;
                                                });
                                                if (pearl) {
                                                    pearl.clasp_dwt = _.round(this.itemDescription.item.scale_dwt - childWeightCount - pearl.aggregate_dwt, 2);
                                                }
                                                this.$nextTick(() => {
                                                    this.$forceUpdate();
                                                    setTimeout(() => {
                                                        this.$nextTick(() => {
                                                            //this has to be here to prevent diamonds auto pulling in on load.
                                                            this.loading = false;
                                                            if(this.isCoin(this.itemDescription.itemComponents[0])) {
                                                                this.$nextTick(() => {
                                                                    //on next frame to ensure coin dropdown is rendered.
                                                                    //https://codingexplained.com/coding/front-end/vue-js/adding-removing-watchers-dynamically
                                                                    if (this.coinWatcher) {
                                                                        this.coinWatcher(); //I don't get how this unsets it, but it does.
                                                                    }
                                                                    this.coinWatcher = this.$watch(
                                                                        '$refs.coinSelection.pointer',
                                                                        (value) => {
                                                                            this.getCoinImage(this.$refs.coinSelection.filteredOptions[value]);
                                                                        }
                                                                    );
                                                                });
                                                            }
                                                        });
                                                    }, 50);
                                                });
                                                let sClass = _.find(this.itemClasses, ['id', this.itemDescription.itemComponents[0].class_id]);
                                                this.itemDescription.options.styles = [];
                                                _.forEach(this.itemDescription.item.selected_type.styles, (value) => {
                                                    if (!value.coin_definition || !sClass || (sClass.name == value.coin_definition.metal)) {
                                                        // Remove Other from silver coin options
                                                        if (!sClass || !(sClass.name == 'silver' && value.name == 'Other')) {
                                                            this.itemDescription.options.styles.push({
                                                                value: value.id,
                                                                text: value.name,
                                                                rank: value.rank
                                                            });
                                                        }
                                                    }
                                                });
                                            });

                                            

                                            this.$forceUpdate();

                                            // Clone original for change detection
                                            this.originalItemDescription = _.cloneDeep(this.itemDescription);
                                        });

                                    } else if (this.$route.query.after_save) {
                                        this.$nextTick(() => {
                                            this.loading = false;
                                            this.working = false;
                                            this.activeItem = false;
                                            this.from = this.$route.query.after_save;
                                        });
                                    } else { //causaes issue in loading if the above change is set
                                        // skip first change in data
                                        this.$nextTick(() => {
                                            this.loading = false;
                                            this.working = false;
                                            this.activeItem = true;
                                        });
                                    }
                                })
                                .catch((error) => {
                                    if (error.response && error.response.status === 404) {
                                    }
                                    this.$router.replace({path: '/appraisals/not-found/' + this.$route.params.id});
                                });
                        });
            });
        },
        activateImageModal(item) {
            var img = this.createImageURL(item);
            this.$refs.PhotoWidget.renderImage(img);
        },
        getItemOptions() {
            //remove packaging as a direct option
            //todo replace with database 'is visible'
            let visible = [];
            _.forEach(this.itemOptions, (grouping) => {
                let container = {};
                if (true) {//todo validate on type
                    container.name = grouping.name;
                    container.id = grouping.id;
                    container.types = [];
                    _.forEach(grouping.types, (item) => {
                        switch (item.name) {
                            case "Moissanite":
                            case "Cubic Zirconia":
                            case 'Packaging':
                                //remove packaging from list
                                break;
                            default:
                                if (this.itemDescription.item.category_id === 2) {
                                    if (grouping.id === 2) {
                                        container.types.push(item);
                                    }
                                } else {
                                    container.types.push(item);
                                }
                                break;
                        }
                    });
                    visible.push(container);
                }
            });
            return visible;
        },
        toggleCoinManual(value) {
            if (value !== undefined) {
                this.coinWeightManual = value;
            } else {
                this.coinWeightManual = !this.coinWeightManual;
            }
            if (!this.coinWeightManual) {
                let style = _.find(this.itemDescription.item.selected_type.styles, ['id', this.itemDescription.itemComponents[0].style_id]);
                if (style && style.coin_definition) {
                    let coinDefinition = style.coin_definition;
                    if (!this.coinWeightManual) {
                        let calculated =
                            _.round(this.convertTroyOuncesToDWT(coinDefinition.troy_ounce * this.itemDescription.itemComponents[0].quantity), 2);
                        if(calculated - this.itemDescription.item.scale_dwt < .002) { //this triggers reweight process if true!
                            this.itemDescription.itemComponents[0].tare_weight = this.itemDescription.itemComponents[0].net_dwt = this.itemDescription.itemComponents[0].aggregate_dwt = calculated;
                        }
                        else {
                            this.$forceUpdate();
                            this.coinWeightManual = true;
                            this.$refs.GenericPopups.displayError =
                                {
                                    title: 'Error In Weight Calculation, expected Coin weight is higher',
                                    text: 'Predicted Coin Weight excedes the total weight the item by ' + _.round(calculated-this.itemDescription.item.scale_dwt,2) + ' dwt. Weight calculations have been switched to manual mode.'
                                };
                        }
                    }
                }
            }
            this.calculateWeightDistribution();
            this.$forceUpdate();
        },
        meleeWeightCalc() {
            this.calculateWeightDistribution();
            this.$forceUpdate();
        },
        getGemStylesByType(item) {
            //9 is precious
            if (item) {
                return _.map(_.find(_.find(this.itemOptions, ['id', 9]).types, ['id', item]).styles, (value) => {
                    return {
                        value: value.id,
                        text: value.name
                    };
                });
            }
            return [];
        },
        getGemColorByType(item) {
            //if(item) {
            return this.sortAlphabetically(_.map(_.find(this.itemOptions, ['id', 9]).colors, (value) => {
                return {
                    value: value.id,
                    text: value.name
                };
            }), 'text');
            //}
            return [];
        },
        getSyntheticStylesByType(item) {
            //8 is synthetic
            if (item) {
                return this.sortAlphabetically(_.map(_.find(_.find(this.itemOptions, ['id', 8]).types, ['id', item]).styles, (value) => {
                    return {
                        value: value.id,
                        text: value.name
                    };
                }), 'text');
            }
            return [];
        },
        getSyntheticColorByType(item) {
            if (item) {
                return _.map(_.find(this.itemOptions, ['id', 8]).colors, (value) => {
                    return {
                        value: value.id,
                        text: value.name
                    };
                });
            }
            return [];
        },
        getPreciousStoneStylesByType(item) {
            //7 is gem
            if (item) {
                return _.map(_.find(_.find(this.itemOptions, ['id', 7]).types, ['id', item]).styles, (value) => {
                    return {
                        value: value.id,
                        text: value.name
                    };
                });
            }
            return [];
        },
        getPreciousStoneColorByType(item) {
            //if(item) {
            return _.map(_.find(this.itemOptions, ['id', 7]).colors, (value) => {
                return {
                    value: value.id,
                    text: value.name
                };
            });
            //}
            return [];
        },
        getDiamondBrands() {
            return this.sortAlphabetically(_.map(_.find(this.itemOptions, ['id', 13]).brands, (value) => {
                return {
                    value: value.id,
                    text: value.name
                };
            }), 'text');
        },
        getDiamondConditions() {
            return _.map(_.find(this.itemOptions, ['id', 13]).conditions, (value) => {
                return {
                    value: value.id,
                    text: value.name
                };
            });
        },
        createBaseItem() {
            this.$set(this.itemDescription.itemComponents, 0, _.assign({}, this.itemDescription.itemComponentTemplate));
            this.coinDropDownSelection = null;
            this.itemDescription.coin_packaging_id = null;
        },

        coinSelectIsOpen() {
            this.showCoinPopover = false;
            this.coinDropDownOpen = true;
        },
        setCoinStyleType(coinID) {
            if(coinID) {
                this.getCoinImage(coinID);
                coinID = coinID.value;
                this.itemDescription.itemComponents[0].style_id = coinID;
                this.itemDescription.itemComponents[0].numismatic_value = null;
            }
        },
        getCoinImage(coinID) {
            if(coinID) {
                coinID = coinID.value;
                let coinRegion = this.itemDescription.item.selected_type;
                let coin = coinRegion.styles.filter(item => item.id === coinID);
                if(coin[0]) {
                    this.selectedCoin = coin[0].coin_definition;
                    if (this.coinDropDownOpen) {
                        this.showCoinPopover = true;
                    }
                }
            }
        },
        closeCoinPopover() {
            this.showCoinPopover = false;
        },

        selectTypeShortcut(type_id) {
            _.forEach(this.itemOptions, (i) => {
                _.forEach(i.types, (t) => {
                    if (t.id === type_id) {
                        this.itemDescription.item.selected_type = t;
                        this.$forceUpdate();
                    }
                });
            });
        },

        async resetType() {
            this.itemDescription.itemComponents.splice(1, this.itemDescription.itemComponents.length);
            this.itemDescription.item.selected_type = null;
            this.itemDescription.item.category_id = null;
            this.itemDescription.item.quantity = 1;
            this.itemDescription.item.certificate_number = null;
            this.itemDescription.itemComponents[0].category_id = null;
            this.itemDescription.itemComponents[0].style_id = null;
            this.itemPurityPreset = null;
            this.itemDescription.coin_packaging_id = null;
            this.itemDescription.itemComponents[0].purity = null;
            this.itemDescription.itemComponents[0].purity_type = null;
            this.itemDescription.itemComponents[0].class_id = null;
            this.itemDescription.itemComponents[0].color_id = null;
            this.$forceUpdate();
        },

        getClassName: function (item) {
            if (item.class_id == 11 && item.type_id !== null) {
                var result = _.find(this.componentOptions.precious_stone.types, ['value', item.type_id]);
                if (result != null) {
                    return result.text;
                }
            }
            switch (item.category_id) {
                case 9:
                    return 'Gem';
                case 3:
                    if (item.style_id === 50) {
                        return 'Watch Movement';
                    }
                    break;
                case 8:
                    if (item.type_id == 51) {
                        return 'Pearls';
                    } else {
                        return 'Synthetic Stone';
                    }
            }
            if (item.type_id == 49 && item.type_id !== null) {
                return 'Packaging';
            } else if (item.class_id !== null) {
                let result = _.find(this.itemClasses, ['id', item.class_id]);
                if (result) {
                    return result.name;
                }
            }
            if (item.category_id !== null) {
                return _.find(this.itemOptions, ['id', item.category_id]).name;
            } else {
                return null;
            }
        },

        // Additions && Subtractions
        deleteItem(index) {
            if (this.isCoin(this.itemDescription.itemComponents[0]) && ! this.coinWeightManual) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'Coin is still in automatic mode, Please switch to manual mode to remove package weight.'
                    };
                return;
            }
            if(index == this.itemDescription.coin_packaging_id) {
                this.itemDescription.coin_packaging_id = null;
            }
            if(this.itemDescription.coin_packaging_id && index < this.itemDescription.coin_packaging_id) {
                this.itemDescription.coin_packaging_id--;
            }
            console.log(this.itemDescription.coin_packaging_id, index);
            this.itemDescription.itemComponents.splice(index, 1);
            this.calculateWeightDistribution();
        },
        addMelee() {
            if (this.isCoin(this.itemDescription.itemComponents[0]) && ! this.coinWeightManual) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'Coin is still in automatic mode, Please switch to manual mode to add other components.'
                    };
                return;
            }
            if (this.itemDescription.item.scale_dwt !== null && this.itemDescription.item.scale_dwt !== undefined) {
                this.itemDescription.itemComponents.push(_.assign({}, this.itemDescription.itemComponentTemplate));
                let index = this.itemDescription.itemComponents.length - 1;
                this.itemDescription.itemComponents[index].category_id = 13;
                this.itemDescription.itemComponents[index].class_id = 9;
                this.itemDescription.itemComponents[index].type_id = 52;
                this.itemDescription.itemComponents[index].tare_weight = 1;
                this.itemDescription.itemComponents[index].weight_type = 'points';
                this.additionVisible = true;
            } else {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'The initial weight of the item must be recorded before adding a child component.'
                    };
            }
        },
        isMelee(itemComponent) {
            if (itemComponent) {
                return itemComponent.category_id === 13 && itemComponent.type_id === 52;
            }
            return false;
        },
        addDiamond() {
            if (this.isCoin(this.itemDescription.itemComponents[0]) && ! this.coinWeightManual) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'Coin is still in automatic mode, Please switch to manual mode to add other components.'
                    };
                return;
            }
            if (this.itemDescription.item.scale_dwt !== null && this.itemDescription.item.scale_dwt !== undefined) {
                this.itemDescription.itemComponents.push(_.assign({}, this.itemDescription.itemComponentTemplate));
                let index = this.itemDescription.itemComponents.length - 1;
                this.itemDescription.itemComponents[index].category_id = 13;
                this.itemDescription.itemComponents[index].class_id = 8;
                this.itemDescription.itemComponents[index].type_id = 33;
                this.itemDescription.itemComponents[index].tare_weight = 0.18;
                this.itemDescription.itemComponents[index].weight_type = 'carats';
                this.calculateItemComponentDWT(this.itemDescription.itemComponents[index]);
                this.additionVisible = true;
            } else {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'The initial weight of the item must be recorded before adding a child component.'
                    };
            }
        },
        isDiamond(itemComponent) {
            if (itemComponent) {
                return itemComponent.category_id === 13 && itemComponent.type_id === 33;
            }
            return false;
        },
        isCoin(itemComponent) {
            if (itemComponent) {
                return itemComponent.category_id === 2;
            }
            return false;
        },
        isChildHold() {
            for (var i = 0; i < this.itemDescription.itemComponents.length; i++) {
                var target = this.itemDescription.itemComponents[i];
                if (this.isDiamond(target) && target.tare_weight >= .18) {
                    return true;
                }
            }
            return false;
        },
        addPreciousStone() {
            if (this.isCoin(this.itemDescription.itemComponents[0]) && ! this.coinWeightManual) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'Coin is still in automatic mode, Please switch to manual mode to add other components.'
                    };
                return;
            }
            if (this.itemDescription.item.scale_dwt !== null && this.itemDescription.item.scale_dwt !== undefined) {
                this.itemDescription.itemComponents.push(_.assign({}, this.itemDescription.itemComponentTemplate));
                let index = this.itemDescription.itemComponents.length - 1;
                this.itemDescription.itemComponents[index].category_id = 7;
                this.itemDescription.itemComponents[index].class_id = 11;
                this.itemDescription.itemComponents[index].weight_type = 'dwt';
                this.additionVisible = true;
            } else {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'The initial weight of the item must be recorded before adding a child component.'
                    };
            }
        },
        isPreciousStone(itemComponent) {
            if (itemComponent) {
                return itemComponent.category_id === 7 && itemComponent.class_id === 11;
            }
            return false;
        },
        addMixedMetal() {
            if (this.isCoin(this.itemDescription.itemComponents[0]) && ! this.coinWeightManual) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'Coin is still in automatic mode, Please switch to manual mode to add other components.'
                    };
                return;
            }
            if (this.itemDescription.item.scale_dwt !== null && this.itemDescription.item.scale_dwt !== undefined) {
                this.itemDescription.itemComponents.push(_.assign({}, this.itemDescription.itemComponentTemplate));
                let index = this.itemDescription.itemComponents.length - 1;
                this.itemDescription.itemComponents[index].category_id = 5;
                this.itemDescription.itemComponents[index].quantity = 1;
                this.itemDescription.itemComponents[index].weight_type = 'dwt';
                this.itemDescription.itemComponents[index].purities = [];
                this.$set(this.itemDescription.itemComponents[index], 'purityPreset', null);
                this.additionVisible = true;
            } else {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'The initial weight of the item must be recorded before adding a child component.'
                    };
            }
        },
        isMixedMetal(itemComponent) {
            if (itemComponent) {
                return itemComponent.category_id === 5;
            }
            return false;
        },
        addGem() {
            if (this.isCoin(this.itemDescription.itemComponents[0]) && ! this.coinWeightManual) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'Coin is still in automatic mode, Please switch to manual mode to add other components.'
                    };
                return;
            }
            if (this.itemDescription.item.scale_dwt !== null && this.itemDescription.item.scale_dwt !== undefined) {
                this.itemDescription.itemComponents.push(_.assign({}, this.itemDescription.itemComponentTemplate));
                let index = this.itemDescription.itemComponents.length - 1;
                this.itemDescription.itemComponents[index].category_id = 9;
                this.itemDescription.itemComponents[index].class_id = 11;
                this.itemDescription.itemComponents[index].weight_type = 'dwt';
                this.subtractionsVisible = true;
            } else {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'The initial weight of the item must be recorded before adding a child component.'
                    };
            }
        },
        isGem(itemComponent) {
            if (itemComponent) {
                return itemComponent.category_id === 9;
            }
            return false;
        },
        addPackaging() {
            if (this.isCoin(this.itemDescription.itemComponents[0]) && ! this.coinWeightManual && this.itemDescription.coin_packaging_id) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'Coin is still in automatic mode, Please switch to manual mode to add other components.'
                    };
                return;
            }
            if (this.itemDescription.item.scale_dwt !== null && this.itemDescription.item.scale_dwt !== undefined) {
                this.itemDescription.itemComponents.push(_.assign({}, this.itemDescription.itemComponentTemplate));
                let index = this.itemDescription.itemComponents.length - 1;
                this.itemDescription.itemComponents[index].class_id = 11;
                this.itemDescription.itemComponents[index].category_id = 11;
                this.itemDescription.itemComponents[index].type_id = 49;
                this.itemDescription.itemComponents[index].weight_type = 'dwt';
                this.subtractionsVisible = true;
                return index;
            } else {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'The initial weight of the item must be recorded before adding a child component.'
                    };
            }
        },
        isPackaging(itemComponent) {
            if (itemComponent) {
                return itemComponent.category_id === 11 && itemComponent.type_id === 49;
            }
            return false;
        },
        addWatchMovement() {
            if (this.isCoin(this.itemDescription.itemComponents[0]) && ! this.coinWeightManual) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'Coin is still in automatic mode, Please switch to manual mode to add other components.'
                    };
                return;
            }
            if (this.itemDescription.item.scale_dwt !== null && this.itemDescription.item.scale_dwt !== undefined) {
                this.itemDescription.itemComponents.push(_.assign({}, this.itemDescription.itemComponentTemplate));
                let index = this.itemDescription.itemComponents.length - 1;
                this.itemDescription.itemComponents[index].category_id = 3;
                this.itemDescription.itemComponents[index].class_id = 11;
                this.itemDescription.itemComponents[index].style_id = 50;
                this.itemDescription.itemComponents[index].weight_type = 'dwt';
                this.subtractionsVisible = true;
            } else {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'The initial weight of the item must be recorded before adding a child component.'
                    };
            }
        },
        isWatchMovement(itemComponent) {
            if (itemComponent) {
                return itemComponent.category_id === 3 && itemComponent.style_id === 50;
            }
            return false;
        },
        addPearls() {
            if (this.isCoin(this.itemDescription.itemComponents[0]) && ! this.coinWeightManual) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'Coin is still in automatic mode, Please switch to manual mode to add other components.'
                    };
                return;
            }
            if (this.itemDescription.item.scale_dwt !== null && this.itemDescription.item.scale_dwt !== undefined) {
                this.itemDescription.itemComponents.push(_.assign({}, this.itemDescription.itemComponentTemplate));
                let index = this.itemDescription.itemComponents.length - 1;
                this.itemDescription.itemComponents[index].category_id = 8;
                this.itemDescription.itemComponents[index].class_id = 11;
                this.itemDescription.itemComponents[index].type_id = 51;
                this.itemDescription.itemComponents[index].weight_type = 'dwt';
                this.$set(this.itemDescription.itemComponents[index], 'clasp_dwt', 0);
                this.subtractionsVisible = true;
            } else {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'The initial weight of the item must be recorded before adding a child component.'
                    };
            }
        },
        isPearls(itemComponent) {
            if (itemComponent) {
                return itemComponent.category_id === 8 && itemComponent.type_id == 51;
            }
            return false;
        },
        addSyntheticStone() {
            if (this.isCoin(this.itemDescription.itemComponents[0]) && ! this.coinWeightManual) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'Coin is still in automatic mode, Please switch to manual mode to add other components.'
                    };
                return;
            }
            if (this.itemDescription.item.scale_dwt !== null && this.itemDescription.item.scale_dwt !== undefined) {
                this.itemDescription.itemComponents.push(_.assign({}, this.itemDescription.itemComponentTemplate));
                let index = this.itemDescription.itemComponents.length - 1;
                this.itemDescription.itemComponents[index].category_id = 8;
                this.itemDescription.itemComponents[index].class_id = 11;
                this.itemDescription.itemComponents[index].weight_type = 'dwt';
                this.subtractionsVisible = true;
            } else {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'The initial weight of the item must be recorded before adding a child component.'
                    };
            }
        },
        isSyntheticStone(itemComponent) {
            if (itemComponent) {
                return itemComponent.category_id === 8 && itemComponent.type_id != 51;
            }
            return false;
        },
        addOther() {
            if (this.isCoin(this.itemDescription.itemComponents[0]) && ! this.coinWeightManual) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'Coin is still in automatic mode, Please switch to manual mode to add other components.'
                    };
                return;
            }
            if (this.itemDescription.item.scale_dwt !== null && this.itemDescription.item.scale_dwt !== undefined) {
                this.itemDescription.itemComponents.push(_.assign({}, this.itemDescription.itemComponentTemplate));
                let index = this.itemDescription.itemComponents.length - 1;
                this.itemDescription.itemComponents[index].category_id = 1;
                this.itemDescription.itemComponents[index].class_id = 11;
                this.itemDescription.itemComponents[index].type_id = 10;
                this.itemDescription.itemComponents[index].weight_type = 'dwt';
                this.subtractionsVisible = true;
            } else {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weight of your item',
                        text: 'The initial weight of the item must be recorded before adding a child component.'
                    };
            }
        },
        isOther(itemComponent) {
            if (itemComponent) {
                return itemComponent.category_id === 1 && itemComponent.type_id === 10;
            }
            return false;
        },
        isMetal: function (itemComponent) {
            if (itemComponent && itemComponent.category_id !== null) {
                let category_id = itemComponent.category_id;
                return category_id !== 7 && category_id !== 13 && category_id !== 8 && category_id !== 9 && category_id !== 12;
            }
            return false;
        },
        isNonPreciousMetal: function (itemComponent) {
            if (itemComponent) {
                return itemComponent.class_id === 11;
            }
            return false;
        },
        isGoldType: function (itemComponent) {
            if (itemComponent) {
                //return true if item is gold or gold filled
                return itemComponent.class_id == 1 || itemComponent.class_id == 3;
            }
            return false;
        },
        isOtherMetal: function (itemComponent) {
            if (itemComponent) {
                //return true if item is gold or gold filled
                return itemComponent.class_id == 11;
            }
            return false;
        },
        setMixedMetalPurityOptions(index, class_id) {
            let item_class = _.find(this.itemClasses, ['id', parseInt(class_id)]);
            if (item_class) {
                this.itemDescription.itemComponents[index].purities = [{value: null, text: ''},];
                _.forEach(item_class.purities, (value) => {
                    this.itemDescription.itemComponents[index].purities.push({
                        value: value.id,
                        text: value.purity + ' ' + value.purity_type
                    });
                });
                this.itemDescription.itemComponents[index].purities.push({value: 'other', text: 'Other'});
            }
        },

        setMixedMetalPurityValue(index, purity_id) {
            if (purity_id !== 'other' && purity_id !== null) {
                let item_class = _.find(this.itemClasses, ['id', this.itemDescription.itemComponents[index].class_id]);
                if (item_class) {
                    let purity_object = _.find(item_class.purities, ['id', purity_id]);
                    console.log("PurityObject", purity_object, item_class.purities, this.itemDescription.itemComponents[index].purity);
                    if(purity_object) {
                        this.itemDescription.itemComponents[index].purity = purity_object.purity;
                        this.itemDescription.itemComponents[index].purity_type = purity_object.purity_type;
                    }
                    else {
                        this.itemDescription.itemComponents[index].purity = null;
                        this.itemDescription.itemComponents[index].purity_type = null;
                    }
                } else {
                    this.itemDescription.itemComponents[index].purity = null;
                    this.itemDescription.itemComponents[index].purity_type = null;
                }
            } else if (purity_id === null) {
                this.itemDescription.itemComponents[index].purity = null;
                this.itemDescription.itemComponents[index].purity_type = null;
            }
            this.$forceUpdate(); //some ui elements choose to not update.
        },

        // Weight and Photo capture
        captureWeightAndPhoto() {
            this.itemDescription.item.scale_dwt = this.scaleWeight;
            if(this.isCoin(this.itemDescription.itemComponents[0])) {
                if(this.coinWeightManual) {
                    this.toggleCoinManual();
                } else {
                    this.calculateWeightDistribution();
                }
            }
            if(this.isMelee(this.itemDescription.itemComponents[0]) || this.isDiamond(this.itemDescription.itemComponents[0])) {
                this.meleeWeightCalc();
            }
            this.takePhoto();
            this.scaleSaved = true;
        },

        recaptureWeightAndPhoto() {
            if(!this.itemDescription.item.id) {
                this.nextImageSaveIndex = 0;
                this.itemDescription.pictures.splice(0, 1);
            }
            this.captureWeightAndPhoto();
        },

        takePhoto() {
            this.$refs.picturePreview.takePhoto();
        },
        convertImg(blob) {
            const width = 2048;
            const height = 1536;


            let canvas = document.createElement('canvas');  // Dynamically Create a Canvas Element
            canvas.width = width;  // Set the width of the Canvas
            canvas.height = height;  // Set the height of the Canvas
            let ctx = canvas.getContext('2d');  // Get the "context" of the canvas
            let img = document.createElement('img');
            var _this = this;
            img.onload = function () {
                ctx.drawImage(img, 0, 0, width, height);
                return canvas.toBlob((blob) => {
                    _this.savePhoto(blob,_this.nextImageSaveIndex);
                    _this.nextImageSaveIndex = null;
                }, 'image/jpeg', 0.75);
            };
            img.src = this.createImageURL(blob);
        },
        savePhotoAsync(blob) {
            this.convertImg(blob);
        },
        savePhoto(blob, index) {
            if (index != null || index != undefined) {
                this.itemDescription.pictures.splice(index, 0, blob); //remove no elements, insert blob here.
            } else {
                this.itemDescription.pictures.push(blob);
            }
        },

        createImageURL(blob) {
            if (blob instanceof Blob) {
                return URL.createObjectURL(blob);
            }
            return blob.url; //its a URL
        },

        removePhoto(index) {
            if (!(this.itemDescription.pictures[index] instanceof Blob)) {
                this.itemDescription.deletedPictureUrls.push(this.itemDescription.pictures[index]);
            }
            this.itemDescription.pictures.splice(index, 1);
        },

        // Weight calculations
        calculateWeightDistribution() {
            let baseComponent = this.itemDescription.itemComponents[0];

            if (baseComponent) {
                if(!this.coinWeightManual) {
                    if (this.isCoin(baseComponent) && baseComponent.aggregate_dwt < this.itemDescription.item.scale_dwt && !this.itemDescription.coin_packaging_id) {
                        console.log('add packaging');
                        this.itemDescription.coin_packaging_id = this.addPackaging();
                    } else if (this.isCoin(baseComponent) && baseComponent.aggregate_dwt >= this.itemDescription.item.scale_dwt) {
                        if(this.itemDescription.coin_packaging_id) {
                            console.log('delete packaging');
                            let packaging_id = this.itemDescription.coin_packaging_id;
                            this.itemDescription.coin_packaging_id = null;
                            this.deleteItem(packaging_id);
                        }
                        if (baseComponent.aggregate_dwt - this.itemDescription.item.scale_dwt > .002) {
                            this.coinWeightManual = true;
                        }
                    }
                    if (this.isCoin(baseComponent) && this.itemDescription.coin_packaging_id && this.itemDescription.itemComponents[this.itemDescription.coin_packaging_id]) {
                        let packaging = this.itemDescription.itemComponents[this.itemDescription.coin_packaging_id];
                        let difference = _.round(this.itemDescription.item.scale_dwt - baseComponent.aggregate_dwt, 2);
                        if (baseComponent.aggregate_dwt == 0) {
                            this.coinWeightManual = true;
                            let packaging_id = this.itemDescription.coin_packaging_id;
                            this.itemDescription.coin_packaging_id = null;
                            this.deleteItem(packaging_id);
                        } else if (difference > 0) {
                            packaging.tare_weight = _.round(difference, 2);
                            packaging.net_dwt = _.round(difference, 2);
                            packaging.aggregate_dwt = _.round(difference, 2);
                        }
                    }
                }

                if((this.isMelee(baseComponent) || this.isDiamond(baseComponent)) && baseComponent.aggregate_dwt < this.itemDescription.item.scale_dwt && !this.itemDescription.coin_packaging_id) {
                    this.itemDescription.coin_packaging_id = this.addPackaging();
                }
                else if((this.isMelee(baseComponent) || this.isDiamond(baseComponent)) && this.itemDescription.itemComponents[this.itemDescription.coin_packaging_id]) {
                    if(this.itemDescription.coin_packaging_id) {
                        let packaging_id = this.itemDescription.coin_packaging_id;
                        this.itemDescription.coin_packaging_id = null;
                        this.deleteItem(packaging_id);
                    }
                }
                if ((this.isMelee(baseComponent) || this.isDiamond(baseComponent)) && this.itemDescription.coin_packaging_id && this.itemDescription.itemComponents[this.itemDescription.coin_packaging_id]) {
                    let packaging = this.itemDescription.itemComponents[this.itemDescription.coin_packaging_id];
                    let difference = _.round(this.itemDescription.item.scale_dwt - baseComponent.aggregate_dwt, 2);
                    if (difference > 0) {
                        packaging.tare_weight = _.round(difference, 2);
                        packaging.net_dwt = _.round(difference, 2);
                        packaging.aggregate_dwt = _.round(difference, 2);
                    }
                }


                if (!this.isMelee(baseComponent) && !this.isDiamond(baseComponent) && !this.isPreciousStone(baseComponent) && !this.isSyntheticStone(baseComponent) && !this.isGem(baseComponent) && !(this.isCoin(baseComponent) && !this.coinWeightManual)) {
                    baseComponent.tare_weight = _.round(this.itemDescription.item.scale_dwt, 2);
                    baseComponent.net_dwt = _.round((this.itemDescription.item.scale_dwt !== 0 ? (this.itemDescription.item.scale_dwt) : 0), 2);

                    // TODO: should this be calculated from the net weight instead and compared to scale weight for sanity?
                    baseComponent.aggregate_dwt = _.round(this.itemDescription.item.scale_dwt, 2);
                    baseComponent.weight_type = 'dwt';
                }
                // Save any pearls for use after aggregating
                let pearls = null;

                // Loop through each child component and calculate the base component actual dwt
                _.forEach(this.itemDescription.itemComponents, (item, index) => {
                    if (index !== 0 && !(this.isCoin(baseComponent) && !this.coinWeightManual) && !this.isMelee(baseComponent) && !this.isDiamond(baseComponent)) {
                        if (baseComponent.weight_type === 'dwt') {
                            baseComponent.tare_weight = _.round(baseComponent.net_dwt - item.aggregate_dwt, 2);
                        }
                        baseComponent.net_dwt = _.round(baseComponent.net_dwt - item.aggregate_dwt, 2);
                        baseComponent.aggregate_dwt = _.round(baseComponent.aggregate_dwt - item.aggregate_dwt, 2);
                        baseComponent.weight_type = 'dwt';

                        if (this.isPearls(item)) {
                            pearls = item;
                        }
                        if (baseComponent.net_dwt < 0) {
                            this.$refs.GenericPopups.displayError =
                                {
                                    title: 'Error In Weight Calculation',
                                    text: 'You value additions and weight deductions exceed the total weight the item by ' + (-baseComponent.net_dwt) + ' dwt. Please confirm the total weight for your item.'
                                };
                        }
                    }
                });

                // If base material calculation under-runs the defined clasp weight, take the difference out of the pearls dwt.
                if (pearls) {
                    let difference = _.round(pearls.clasp_dwt - baseComponent.aggregate_dwt, 2);
                    if (difference > 0) {
                        baseComponent.tare_weight = _.round(baseComponent.tare_weight + difference, 2);
                        baseComponent.net_dwt = _.round(baseComponent.net_dwt + difference, 2);
                        baseComponent.aggregate_dwt = _.round(baseComponent.aggregate_dwt + difference, 2);
                        pearls.tare_weight = _.round(pearls.tare_weight - difference, 2);
                        pearls.net_dwt = _.round(pearls.net_dwt - difference, 2);
                        pearls.aggregate_dwt = _.round(pearls.aggregate_dwt - difference, 2);
                    }
                }
            }
        },
        calculateItemDifferenceChangePackage(baseItem, packages, value) {
            let diff = baseItem.aggregate_dwt - _.round(value, 2);
            packages.tare_weight = _.round(packages.tare_weight + diff,2);
            if(packages.tare_weight < 0) {
                packages.tare_weight = 0;
            }
            this.calculateItemComponentDWT(packages);
        },
        calculateItemComponentDWT(item) {
            // Handle diamond/melee swap out
            if (this.isMelee(item) && item.tare_weight >= 18) {
                item.category_id = 13;
                item.class_id = 8;
                item.type_id = 33;
                item.tare_weight = this.convertPointsToCarats(item.tare_weight);
                item.weight_type = 'carats';
                if (this.itemDescription.itemComponents[0].type_id == 33 && this.itemDescription.itemComponents[0].category_id == 13) {
                    let holdWeight = item.tare_weight;
                    this.selectTypeShortcut(33);
                    this.$nextTick(function () {
                        item.tare_weight = _.round(holdWeight, 2);
                    });
                }
            } else if (this.isDiamond(item) && item.tare_weight < 0.18 && item.tare_weight !== 0 && item.tare_weight !== '') {
                item.category_id = 13;
                item.class_id = 9;
                item.type_id = 52;
                item.tare_weight = this.convertCaratsToPoints(item.tare_weight);
                item.weight_type = 'points';
                if (this.itemDescription.itemComponents[0].type_id == 52 && this.itemDescription.itemComponents[0].category_id == 13) {
                    let holdWeight = item.tare_weight;
                    this.selectTypeShortcut(52);
                    this.$nextTick(function () {
                        item.tare_weight = _.round(holdWeight, 2);
                    });
                }
            }

            if (this.isPearls(item)) {
                //let remainingItemsDWT = _.sumBy(this.itemDescription.itemComponents, 'aggregate_dwt');
                item.tare_weight = _.round(this.itemDescription.item.scale_dwt - item.clasp_dwt, 2);
            }
            if ((this.isSyntheticStone(item) || this.isGem(item) || this.isPreciousStone(item)) && (item.weight_type == 'carats' || item.weight_type == 'points')) {
                if (item.tare_weight >= 18 && item.weight_type == 'points') {
                    item.weight_type = 'carats';
                    item.tare_weight = this.convertPointsToCarats(item.tare_weight);
                } else if (item.tare_weight < .18 && item.weight_type == 'carats') {
                    item.weight_type = 'points';
                    item.tare_weight = this.convertCaratsToPoints(item.tare_weight);
                }
            }
            // Calculate DWT weights
            if (item.weight_type === 'points') {
                item.net_dwt = this.convertPointsToDWT(item.tare_weight);
                item.aggregate_dwt = _.round(this.convertPointsToDWT(item.tare_weight * item.quantity), 2);
            } else if (item.weight_type === 'carats') {
                item.net_dwt = this.convertCaratsToDWT(item.tare_weight);
                item.aggregate_dwt = _.round(this.convertCaratsToDWT(item.tare_weight * item.quantity), 2);
            } else {
                item.net_dwt = _.round(item.tare_weight, 2);
                item.aggregate_dwt = _.round(item.tare_weight, 2);
            }

            // Recalculate base component weight
            this.calculateWeightDistribution();
        },

        convertPointsToDWT(points_value) {
            return _.round(((points_value / 7.776) / 100), 2);
        },

        convertCaratsToDWT(carats_value) {
            return _.round((carats_value / 7.776), 2);
        },

        convertDWTToCarats(dwt_value) {
            return _.round((dwt_value * 7.776), 2);
        },

        convertPointsToCarats(points_value) {
            return _.round((points_value / 100), 2);
        },

        convertCaratsToPoints(carats_value) {
            return _.round((carats_value * 100), 2);
        },

        convertTroyOuncesToDWT(troy_ounces_value) {
            return _.round((troy_ounces_value * 20), 4);
        },
        getDiamondEAVValueByIndex(index) {
            this.getDiamondEAVValue(this.itemDescription.itemComponents[index]);
        },
        getDiamondEAVValue(item) {
            if (item.clarity_id && item.style_id && item.color_id && item.quantity && !this.loading && !(this.isDiamond(item) && item.tare_weight > .6995)) {
                this.$http.get('/item/diamondeav', {params: item}).then((response) => {
                    item.numismatic_value = parseFloat(response.data);
                });
            } else {
                item.numismatic_value = 0;
            }
        },
        throttledGetDiamondEAVValue(index) {
            if (this.preventEAVEdit && !this.loading) {
                if(!this.runEAV[index]) {
                    this.runEAV[index] = _.throttle(() => {
                        this.getDiamondEAVValueByIndex(index);
                    }, 1000);
                }
                this.runEAV[index]();
            }
        },
        // Save or Cancel inventory item
        saveItem() {
            return new Promise(resolve => {
                this.working = true;
                this.saving = true;
                this.savingStepCount = this.itemDescription.pictures.length + 2; // Account for pictures array and saving steps
                this.savingProgress = 1;
                this.savingStatus = 'Saving Item and Components...';
                this.$http.post('/orders/' + this.order.id + '/inventory', {
                    data: {
                        item: this.itemDescription.item,
                        itemComponents: this.itemDescription.itemComponents,
                        bag_type_id: this.hold_bag_id,
                    }
                })
                    .then(async (response) => {
                        let itemID = response.data.data.id;
                        let i = 0;
                        let t = this.itemDescription.pictures.length;

                        this.savingProgress++;

                        // Update item description to include saved id and aggregate dwt
                        this.itemDescription.item.id = itemID;
                        this.itemDescription.item.aggregate_dwt = response.data.data.aggregate_dwt;

                        // Print hold label
                        if (this.itemDescription.item.inventory_status_id === 2) {

                            // Only print hold label if item has changed
                            let printLabel = false;

                            if (this.originalItemDescription) {
                                let originalChildren = this.originalItemDescription.itemComponents;
                                let itemChanged = false;

                                if (!_.isEqual(
                                    _.omit(this.originalItemDescription.item, ['aggregate_dwt', 'numismatic_value', 'type']),
                                    _.omit(this.itemDescription.item, ['aggregate_dwt', 'numismatic_value', 'type'])
                                )) {
                                    console.log('item changed');
                                    itemChanged = true;
                                }

                                if (originalChildren.length !== this.itemDescription.itemComponents.length) {
                                    console.log('children length changed');
                                    itemChanged = true;
                                } else {
                                    for (let j = 0; j < originalChildren.length; j++) {
                                        let originalChild = _.omit(originalChildren[j], ['dwt', 'net_dwt', 'numismatic_value', 'purities', 'purityPreset', 'style']);
                                        let currentChild = _.omit(this.itemDescription.itemComponents[j], ['dwt', 'net_dwt', 'numismatic_value', 'purities', 'purityPreset', 'style']);

                                        if (!_.isEqual(originalChild, currentChild)) {
                                            console.log('child ' + j + ' changed');
                                            itemChanged = true;
                                            break;
                                        }
                                    }
                                }

                                if (itemChanged) {
                                    printLabel = true;
                                }
                            } else {
                                // If no original item description, assume new item and print label
                                printLabel = true;
                            }

                            if (printLabel) {
                                // Prepare item for label printing
                                let item = this.itemDescription.item;
                                item.children = this.itemDescription.itemComponents;
                                let categoryOptions = _.find(this.itemOptions, ['id', item.children[0].category_id]);
                                item.order = this.order;
                                item.customer = this.order.customer;
                                item.bag = {
                                    bag_type_id: this.hold_bag_id
                                };
                                
                                if (item.brand_id) {
                                    const selectedBrand = _.find(this.itemDescription.options.brands, ['value', item.brand_id]);
                                    item.brand_name = selectedBrand ? selectedBrand.text : null;
                                }

                                item.children.forEach((item) => {
                                    if (item && item.class_id) {
                                        item.class = _.find(this.itemClasses, ['id', item.class_id]);
                                        item.class_name = item.class.name;
                                    }
                                    if (this.itemDescription.item.selected_type && item) {
                                        item.type = this.itemDescription.item.selected_type;
                                        item.type_name = item.type.label_name;
                                    }
                                    if (item && item.style_id) {
                                        item.style = _.find(item.type.styles, ['id', item.style_id]);
                                        if (item.style) {
                                            item.style_name = item.style.name;
                                        } else {
                                            item.style_name = 'n/a';
                                        }
                                    }
                                    if (item && item.color_id) {
                                        item.color = _.find(categoryOptions.colors, ['id', item.color_id]);
                                        if (item.color) {
                                            item.color_name = item.color.name;
                                        } else {
                                            item.color_name = 'n/a';
                                        }
                                    }
                                    if (item && item.clarity_id) {
                                        item.clarity = _.find(categoryOptions.clarities, ['id', item.clarity_id]);
                                        if (item.clarity) {
                                            item.clarity_name = item.clarity.name;
                                        } else {
                                            item.clarity_name = 'n/a';
                                        }
                                    }
                                    if (item && item.certificate_type_id) {
                                        item.certificate = _.find(categoryOptions.certificates, ['id', item.certificate_type_id]);
                                        if (item.certificate) {
                                            item.certificate_name = item.certificate.name;
                                        } else {
                                            item.certificate_name = 'n/a';
                                        }
                                    }
                                });

                                // Print the label
                                this.$label('hold', item);
                                
                                if (item.children[0] && _.find(item.children, function (o) {
                                    return o.class_id === 8 && o.tare_weight > 0.4;
                                })) {
                                    this.$label('hold', item);
                                }
                            }
                        }
                        try {
                            for await (let picture of this.itemDescription.pictures) {
                                console.log('blob', picture);
                                i++;
                                this.savingStatus = 'Saving Picture: ' + i + ' of ' + t + '...';
                                let pictureData = new FormData();
                                pictureData.append('savecount', i);
                                if (picture instanceof Blob) {
                                    pictureData.append('picture', await new Promise(resolve => {
                                            //pictureData.append('picture', picture);
                                            let reader = new FileReader();
                                            reader.onload = () => {
                                                resolve(reader.result);
                                            };
                                            reader.readAsDataURL(picture);
                                        }
                                    ));
                                    console.log(picture);
                                    await this.$http.post('/inventory/' + itemID + '/savepicture', pictureData)
                                        .then(() => {
                                            this.savingProgress++;
                                        });
                                }
                            }
                        } catch (e) {}

                        t = this.itemDescription.deletedPictureUrls.length;
                        this.savingStepCount += t;
                        i = 0;
                        //TODO delete pictures (from previous saves)
                        for await (let picture of this.itemDescription.deletedPictureUrls) {
                            i++;
                            this.savingStatus = 'Deleting old Picture: ' + i + ' of ' + t + '...';
                            let pictureData = new FormData();
                            pictureData.append('picture', picture);
                            console.log('pic', picture);
                            await this.$http.delete('/media/' + picture.id)
                                .then(() => {
                                    this.savingProgress++;
                                });
                        }
                        this.itemDescription.deletedPictureUrls = [];

                        await this.resetItem();
                        console.log('save item resolved');
                        this.$parent.getInventory(this.order.id);
                        resolve();
                    });
            });
        },

        async saveItemAndAddNew() {
            await this.saveItem();
            this.addNewItem();
        },
        async saveItemQA() {
            await this.saveItem();
            this.$router.push('/quality-assurance/view/' + this.$route.params.id);
        },
        async saveItemKits() {
            await this.saveItem();
            this.$router.push('/kit/view/' + this.$route.params.id);
        },
        async saveItemOfferGen() {
            await this.saveItem();
            this.$router.push('/offer-generation/view/' + this.$route.params.id);
        },


        resetItem() {
            Object.assign(this.itemDescription.item, initialData());
            this.scaleSaved = false;
            this.scaleStable = false;
            this.scaleWeight = null;
            this.working = false;
            this.saving = false;
            this.savingStatus = null;
            this.itemDescription.itemComponents = [];
            this.itemDescription.pictures = [];
            this.itemDescription.deletedPictureUrls = [];
            this.coinWeightManual = false;
            this.activeItem = false;
        },

        addNewItem() {
            this.createBaseItem();
            this.activeItem = true;
            this.itemPurityPreset = null;
            this.hold_bag_id = 2;
            this.$router.push('/appraisals/appraise/' + this.$route.params.id + this.jsonToQueryString(this.$route.query)).catch(() => {});
        },
        sortAlphabetically(arr, field) {
            function actualSort(left, right) {
                return (left[field] || '').toString().localeCompare((right[field] || '').toString());
            }

            return arr.sort(actualSort);
        },
        sortByOrder(arr, field = 'sort_order') {
            function actualSort(left, right) {
                return left[field] < right[field] ? -1 : left[field] == right[field] ? 0 : 1;
            }

            return arr.sort(actualSort);
        },
        jsonToQueryString(json) {
            return '?' +
                Object.keys(json).map(function (key) {
                    return encodeURIComponent(key) + '=' +
                        encodeURIComponent(json[key]);
                }).join('&');
        },

        printLabel(type, item) {
            this.$label(type, item);
        },
        checkBrandHoldStatus() {
            const brandId = this.itemDescription.item.brand_id;
            if (!brandId) return;
            const params = {
                brand_id: brandId,
                type_id: this.itemDescription.item.type_id,
                class_id: this.itemDescription.itemComponents[0] ? this.itemDescription.itemComponents[0].class_id : null,
                style_id: this.itemDescription.itemComponents[0] ? this.itemDescription.itemComponents[0].style_id : null,
            };
            this.$http.post('/inventory/brand-hold-lookup', params).then((response) => {
                const lookup = response.data || {};
                this.itemDescription.item.inventory_status_id = 1;
                if (lookup.matched !== true) {
                    return;
                }
                if (lookup.result) {
                    this.itemDescription.item.inventory_status_id = lookup.result.should_hold === true ? 2 : 1;
                }
            });
        },
    },

    watch: {
        'itemDescription.itemComponents': {
            handler(newValueArr, depre_oldValueArray) { //FIXME why does oldValueArray use the new internal values?
                let oldValueArr = this.itemDescription.oldItemComponents;
                if (newValueArr.length == oldValueArr.length) {
                    for (let i = 0; i < oldValueArr.length; i++) {
                        let oldValue = oldValueArr[i];
                        let newValue = newValueArr[i];
                        if(oldValue.class_id != newValue.class_id && !this.isCoin(oldValue)) {
                            if(i == 0) {
                                this.itemPurityPreset = null;
                            } else {
                                newValue.purity = null;
                                newValue.purityPreset = null;
                            }
                        }
                        if (newValue) {
                            if (this.isCoin(newValue)) {
                                if(!this.loading) { //prevents double issue. (race condition!!)
                                    this.$nextTick(() => {
                                        //reloading coins may not have them in frame yet, forces full load
                                        this.$forceUpdate();
                                        this.$nextTick(() => {
                                            //on next frame to ensure coin dropdown is rendered.
                                            //https://codingexplained.com/coding/front-end/vue-js/adding-removing-watchers-dynamically
                                            if (this.coinWatcher) {
                                                this.coinWatcher(); //I don't get how this unsets it, but it does.
                                            }
                                            this.coinWatcher = this.$watch(
                                                '$refs.coinSelection.pointer',
                                                (value) => {
                                                    this.getCoinImage(this.$refs.coinSelection.filteredOptions[value]);
                                                }
                                            );
                                        });
                                    });
                                }
                                //todo may need to apply to more than just coins later. this is reallocate styles on class_id change
                                if(newValue.class_id != oldValue.class_id) {
                                    let sClass = _.find(this.itemClasses, ['id', parseInt(newValue.class_id)]);
                                    this.itemDescription.options.styles = [];
                                    _.forEach(this.itemDescription.item.selected_type.styles, (value) => {
                                        if (!value.coin_definition || !sClass || (sClass.name == value.coin_definition.metal)) {
                                            // Remove Other from silver coin options
                                            if (!sClass || !(sClass.name == 'silver' && value.name == 'Other')) {
                                                this.itemDescription.options.styles.push({
                                                    value: value.id,
                                                    text: value.name,
                                                    rank: value.rank
                                                });
                                            }
                                        }
                                    });
                                    //clear coin selected on class change. if there was a previous class decided
                                    if(oldValue.class_id) {
                                        this.itemDescription.itemComponents[0].style_id = null;
                                        this.coinDropDownSelection = null;
                                    }
                                }
                                // Default to Hold or Melt based on metal type (precious/non-precious)
                                if(newValue.class_id != oldValue.class_id) {
                                    if (!this.isOtherMetal(this.itemDescription.itemComponents[0])) {
                                        this.itemDescription.item.inventory_status_id = 2;
                                    } else {
                                        this.itemDescription.item.inventory_status_id = 1;
                                    }
                                }

                                // Set values based on Coin Definition
                                let style = _.find(this.itemDescription.item.selected_type.styles, ['id', newValue.style_id]);
                                if (style && style.coin_definition) {
                                    let coinDefinition = style.coin_definition;
                                    newValue.class_id = _.find(this.itemClasses, ['name', coinDefinition.metal]).id;

                                    // Set purity
                                    if (newValue.style_id != oldValue.style_id) {
                                        this.itemPurityPreset = 'other';
                                        if (coinDefinition.purity_type === 'K') {
                                            newValue.purity_type = 'kt';
                                            newValue.purity = coinDefinition.purity_karat;
                                        } else if (coinDefinition.purity_type === '%') {
                                            newValue.purity_type = '%';
                                            newValue.purity = coinDefinition.purity_percent;
                                        } else {
                                            newValue.purity_type = null;
                                            newValue.purity = 0;
                                        }
                                    }

                                    // Set Hold or Melt based on coin definition
                                    if (coinDefinition.auto_set_to_hold === 1) {
                                        this.itemDescription.item.inventory_status_id = 2;
                                    } else {
                                        this.itemDescription.item.inventory_status_id = 1;
                                    }

                                    if (!this.coinWeightManual || newValue.quantity != oldValue.quantity || newValue.style_id != oldValue.style_id) {
                                        let calculated =
                                            _.round(this.convertTroyOuncesToDWT(coinDefinition.troy_ounce * newValue.quantity), 2);
                                        if(calculated - this.itemDescription.item.scale_dwt < .002) { //this triggers reweight process if true!
                                            newValue.tare_weight = newValue.net_dwt = newValue.aggregate_dwt =
                                                calculated;
                                            this.coinWeightManual = false;
                                        }
                                        else if (!this.coinWeightManual) {
                                            this.coinWeightManual = true;
                                            this.$refs.GenericPopups.displayError =
                                                {
                                                    title: 'Error In Weight Calculation, expected Coin weight is higher',
                                                    text: 'Predicted Coin Weight excedes the total weight the item by ' + _.round(calculated-this.itemDescription.item.scale_dwt,2) + ' dwt. Weight calculations have been switched to manual mode.'
                                                };
                                            this.$forceUpdate();
                                        }
                                    }
                                } else if (this.isCoin(this.itemDescription.itemComponents[0])) {
                                    this.coinWeightManual = true;
                                }
                            }
                            else if ((this.isMetal(newValue) || this.isNonPreciousMetal(newValue))) {
                                if (newValue.class_id != oldValue.class_id) { //class id change.
                                    let color_opt = null;
                                    switch (parseInt(newValue.class_id)) {
                                        case 1: //gold
                                        case 2: //gold plated
                                        case 3: //gold filled
                                            color_opt = _.find(this.itemDescription.options.colors, ['text', 'Yellow']);
                                            if (oldValue.purity_type != 'kt' && !this.isCoin(newValue)) {
                                                newValue.purity_type = 'kt';
                                                newValue.purity = null; // needs to be set by user.
                                            }
                                            newValue.color_id = color_opt ? color_opt.value : newValue.color_id; //yellow
                                            break;
                                        case 4: //silver
                                        case 5: //silver plated
                                        case 6: //platinum
                                        case 7: //palladium
                                            color_opt = _.find(this.itemDescription.options.colors, ['text', 'White']);
                                            newValue.color_id = color_opt ? color_opt.value : newValue.color_id; //yellow
                                            break;
                                        case 11:
                                            this.itemPurityPreset = 'other';
                                            newValue.purity_type = '%';
                                            newValue.purity = 0;
                                            break;
                                        //fall threw to set %
                                        default:
                                            if (oldValue.purity_type != '%' && !this.isCoin(newValue)) {
                                                newValue.purity_type = '%';
                                                newValue.purity = null; // needs to be set by user.
                                            }
                                            break;
                                    }
                                }
                            } else {
                                newValue.purity_type = null;
                                newValue.purity = null;
                            }
                        }
                        if (this.isDiamond(newValue)) {
                            //diamond is .18 or higher
                            if (this.isDiamond(this.itemDescription.itemComponents[0]) || this.isMelee(this.itemDescription.itemComponents[0]) || this.isCoin(this.itemDescription.itemComponents[0]) || this.isChildHold()) {
                                this.itemDescription.item.inventory_status_id = 2;
                            } else {
                                this.itemDescription.item.inventory_status_id = 1;
                            }
                        }
                        if ((this.isDiamond(newValue) || this.isMelee(newValue)) &&
                            (
                                newValue.color_id != oldValue.color_id ||
                                newValue.quantity != oldValue.quantity ||
                                newValue.style_id != oldValue.style_id ||
                                newValue.brand_id != oldValue.brand_id ||
                                newValue.clarity_id != oldValue.clarity_id ||
                                newValue.condition_id != oldValue.condition_id ||
                                newValue.tare_weight != oldValue.tare_weight
                            )
                        ) {
                            this.throttledGetDiamondEAVValue(i);
                        }
                        if (i === 0 && (newValue.class_id != oldValue.class_id || newValue.style_id != oldValue.style_id) && this.itemDescription.item.brand_id) {
                            this.checkBrandHoldStatus();
                        }
                    }
                } else {
                    //todo hold for when new/remove items is important
                }
                if (!_.every(_.keys(this.itemDescription.oldItemComponents), (currentKey) => {
                    return _.has(newValueArr, currentKey) &&
                        _.isEqual(this.itemDescription.oldItemComponents[currentKey], newValueArr[currentKey]);
                })) { //prevents infinite loop
                    this.calculateWeightDistribution();
                }
                this.itemDescription.oldItemComponents = _.cloneDeep(newValueArr);
            },
            deep: true,
        },
        'itemDescription.item.selected_type'(newValue, oldValue) {
            if (newValue) {
                console.log(newValue.item_category_id);
                let category = _.find(this.itemOptions, ['id', newValue.item_category_id]);
                this.itemDescription.item.type_id = newValue.id;
                this.itemDescription.item.type = newValue;
                this.itemDescription.item.category_id = newValue.item_category_id;
                this.itemDescription.options.styles = [];
                if (oldValue !== null) {
                    this.itemDescription.item.size = null;
                    this.itemSizePreset = null;
                }
                let sClass = _.find(this.itemClasses, ['id', this.itemDescription.itemComponents[0].class_id]);
                _.forEach(newValue.styles, (value) => {
                    if(!value.coin_definition || !sClass || (sClass.name == value.coin_definition.metal)) {
                        // Remove Other from silver coin options
                        if (!sClass || !(sClass.name == 'silver' && value.name == 'Other')) {
                            this.itemDescription.options.styles.push({value: value.id, text: value.name, rank: value.rank});
                        }
                    }
                });
                if(this.isCoin(this.itemDescription.itemComponents[0]) && !this.loading) {
                    this.itemDescription.itemComponents[0].style_id = null;
                    this.itemDescription.itemComponents[0].numismatic_value = null;
                    this.coinDropDownSelection = null;
                }
                this.itemDescription.options.styles = this.sortAlphabetically(this.itemDescription.options.styles, 'text');
                let styleOtherIndex = this.itemDescription.options.styles.findIndex(style => style.text == 'Other');

                if(styleOtherIndex != -1) {
                    let styleOther = this.itemDescription.options.styles.filter(style => style.text == 'Other');
                    this.itemDescription.options.styles.splice(styleOtherIndex, 1);
                    this.itemDescription.options.styles.unshift(styleOther[0]);
                }

                this.itemDescription.options.brands = [{value: null, text: ''},];
                _.forEach(category.brands, (value) => {
                    this.itemDescription.options.brands.push({value: value.id, text: value.name});
                });
                this.itemDescription.options.brands = this.sortAlphabetically(this.itemDescription.options.brands, 'text');

                this.itemDescription.options.certificates = [{value: null, text: ''},];
                _.forEach(category.certificates, (value) => {
                    this.itemDescription.options.certificates.push({value: value.id, text: value.name});
                });

                this.itemDescription.options.colors = [{value: null, text: ''},];
                _.forEach(category.colors, (value) => {
                    this.itemDescription.options.colors.push({value: value.id, text: value.name});
                });
                this.itemDescription.options.colors = this.sortAlphabetically(this.itemDescription.options.colors, 'text');
                let colorNaIndex = this.itemDescription.options.colors.findIndex(style => style.text == 'N/A');
                if(colorNaIndex != -1) {
                    let colorNa = this.itemDescription.options.colors.filter(style => style.text == 'N/A');
                    this.itemDescription.options.colors.splice(colorNaIndex, 1);
                    this.itemDescription.options.colors.splice( 1, 0, colorNa[0] );
                }

                this.itemDescription.options.conditions = [{value: null, text: ''},];
                _.forEach(category.conditions, (value) => {
                    this.itemDescription.options.conditions.push({value: value.id, text: value.name});
                });
                this.itemDescription.options.conditions = this.sortAlphabetically(this.itemDescription.options.conditions, 'text');

                // TODO: Update base component with category and class from selected type
                this.itemDescription.itemComponents[0].type_id = newValue.id;
                this.itemDescription.itemComponents[0].category_id = newValue.item_category_id;
                if (oldValue !== null) {
                    this.itemDescription.itemComponents[0].style_id = null;
                }

                if (newValue.item_category_id == 1 || newValue.item_category_id == 3) {
                    this.itemDescription.item.condition_id = _.filter(this.itemDescription.options.conditions, function (value) {
                        return value.text == 'Fair';
                    })[0].value;
                }

                if (!this._isMetal) {
                    if (newValue.item_category_id === 13 || newValue.item_category_id === 7) {
                        this.itemDescription.itemComponents[0].weight_type = 'carats';
                        this.itemDescription.itemComponents[0].tare_weight = this.convertDWTToCarats(this.itemDescription.item.scale_dwt);

                        // Diamond
                        if (newValue.id === 33) {
                            this.itemDescription.itemComponents[0].class_id = 8;
                            this.itemDescription.itemComponents[0].tare_weight = 0.18;
                            this.itemDescription.itemComponents[0].weight_type = 'carats';
                            this.itemDescription.itemComponents[0].aggregate_dwt = _.round(this.convertCaratsToDWT(this.itemDescription.itemComponents[0].tare_weight * this.itemDescription.itemComponents[0].quantity),2);
                        }
                        if (newValue.id == 52) {
                            this.itemDescription.itemComponents[0].class_id = 9;
                            this.itemDescription.itemComponents[0].tare_weight = 1;
                            this.itemDescription.itemComponents[0].weight_type = 'points';
                            this.itemDescription.itemComponents[0].aggregate_dwt = _.round(this.convertPointsToCarats(this.itemDescription.itemComponents[0].tare_weight * this.itemDescription.itemComponents[0].quantity),2);
                        }
                        // Ruby
                        if (newValue.id === 34) {
                            this.itemDescription.itemComponents[0].class_id = 11;
                            this.itemDescription.itemComponents[0].tare_weight = 1;
                            this.itemDescription.itemComponents[0].weight_type = 'points';
                            this.itemDescription.itemComponents[0].aggregate_dwt = _.round(this.convertPointsToCarats(this.itemDescription.itemComponents[0].tare_weight * this.itemDescription.itemComponents[0].quantity),2);
                        }

                        // Emerald
                        if (newValue.id === 35) {
                            this.itemDescription.itemComponents[0].class_id = 11;
                            this.itemDescription.itemComponents[0].tare_weight = 1;
                            this.itemDescription.itemComponents[0].weight_type = 'points';
                            this.itemDescription.itemComponents[0].aggregate_dwt = _.round(this.convertPointsToCarats(this.itemDescription.itemComponents[0].tare_weight * this.itemDescription.itemComponents[0].quantity),2);
                        }

                        // Sapphire
                        if (newValue.id === 36) {
                            this.itemDescription.itemComponents[0].class_id = 11;
                            this.itemDescription.itemComponents[0].tare_weight = 1;
                            this.itemDescription.itemComponents[0].weight_type = 'points';
                            this.itemDescription.itemComponents[0].aggregate_dwt = _.round(this.convertPointsToCarats(this.itemDescription.itemComponents[0].tare_weight * this.itemDescription.itemComponents[0].quantity),2);
                        }
                        this.additionVisible = true;
                    } else if (newValue.item_category_id == 9) { //gem
                        this.itemDescription.itemComponents[0].tare_weight = 1;
                        this.itemDescription.itemComponents[0].weight_type = 'points';
                        this.itemDescription.itemComponents[0].class_id = 11;
                        this.itemDescription.itemComponents[0].aggregate_dwt = _.round(this.convertPointsToCarats(this.itemDescription.itemComponents[0].tare_weight * this.itemDescription.itemComponents[0].quantity),2);
                        this.subtractionsVisible = true;
                    } else if (newValue.item_category_id == 8) { //synthetic
                        this.itemDescription.itemComponents[0].tare_weight = 1;
                        this.itemDescription.itemComponents[0].weight_type = 'points';
                        this.itemDescription.itemComponents[0].class_id = 11;
                        this.itemDescription.itemComponents[0].aggregate_dwt = _.round(this.convertPointsToCarats(this.itemDescription.itemComponents[0].tare_weight * this.itemDescription.itemComponents[0].quantity),2);
                        this.subtractionsVisible = true;
                    } else if (newValue.item_category_id === 4 && newValue.id === 27) {
                        // Dental
                        //this.itemDescription.itemComponents[0].class_id = 10;
                    } else {
                        // Other
                        this.itemDescription.itemComponents[0].class_id = 11;
                    }
                }
                if(this.isCoin(this.itemDescription.itemComponents[0])) {
                    this.itemPurityPreset = 'other';
                }
                if (this.isDiamond(this.itemDescription.itemComponents[0]) || this.isMelee(this.itemDescription.itemComponents[0]) || (this.isCoin(this.itemDescription.itemComponents[0]) && !this.isOtherMetal(this.itemDescription.itemComponents[0])) || this.isChildHold()) {
                    this.itemDescription.item.inventory_status_id = 2;
                } else {
                    this.itemDescription.item.inventory_status_id = 1;
                }

                // Set default Silver (4) Jewelry (1) purity to 92.5%
                if (this.itemDescription.itemComponents[0].class_id == 4 && newValue.item_category_id == 1) {
                    this.itemDescription.itemComponents[0].purity = '92.50';
                    this.itemDescription.itemComponents[0].purity_type = '%';
                }

                if (this.itemDescription.item.brand_id) {
                    this.checkBrandHoldStatus();
                }

                this.calculateWeightDistribution();
            }
        },
        '_itemClass'(newValue) {
            if (newValue) {
                let item_class = _.find(this.itemClasses, ['id', newValue]);
                if (item_class) {
                    this.itemDescription.options.purities = [{value: null, text: ''},];
                    _.forEach(item_class.purities, (value) => {
                        if(value.purity % 1 == 0) {
                            value.purity = Math.floor(value.purity);
                        }
                        this.itemDescription.options.purities.push({
                            value: value.id,
                            text: value.purity + ' ' + value.purity_type
                        });
                    });
                }
                this.itemDescription.options.purities.push({value: 'other', text: 'Other'});

                // Set default Silver (4) Jewelry (1) purity to 92.5%
                if (newValue == 4 && this.itemDescription.itemComponents[0].category_id == 1) {
                    this.itemDescription.itemComponents[0].purity = '92.50';
                    this.itemDescription.itemComponents[0].purity_type = '%';
                }

            }
        },
        '_itemPurity'(newValue) {
            if (this.itemDescription.itemComponents[0] && newValue === null) {
                this.itemDescription.itemComponents[0].purity = null;
                this.itemDescription.itemComponents[0].purity_type = null;
            }
        },
        'itemDescription.item.scale_dwt'(newValue) {
            if (newValue) {
                this.calculateWeightDistribution();
            }
            let calculated_over = this._orderTotalWeight - this._packagesTotalWeight;
            if (calculated_over > 0 && !this.$refs.WeightValidation.validateBaggingWeight(this._packagesTotalWeight,
                [
                    _.filter(this.$parent.inventoryListSummary, (item) => {
                        return item.id !== this.itemDescription.item.id;
                    }),
                    [{aggregate_dwt: this.itemDescription.item.scale_dwt}]
                ])
            ) {
                this.$refs.GenericPopups.displayError =
                    {
                        title: 'There is an error in calculating the weights for your order',
                        text: 'The items within your order exceed the weight of your Total Inventory weight recorded for this order by ' + (calculated_over.toFixed(2)) + ' dwt. Please confirm your weights.'
                    };
            }
        },
        'itemDescription.item.size'(newValue) {
            console.log(newValue, this.sizeOptionsByItemTypeId[this.itemDescription.item.selected_type.id].find(element => element === newValue));
            let availableItemSizeOption = this.sizeOptionsByItemTypeId[this.itemDescription.item.selected_type.id].find(element => element === newValue);
            console.log(availableItemSizeOption);
            if (availableItemSizeOption) {
                this.itemSizePreset = availableItemSizeOption;
            } else {
                this.itemSizePreset = 'other';
            }
        },
        'itemDescription.item.brand_id'(newValue) {
            if (newValue) {
                this.checkBrandHoldStatus();
            }
        },

    }
};

function initialData() {
    return {
        category_id: null,
        id: null,
        type_id: null,
        selected_type: null,
        brand_id: null,
        selected_brand: null,
        model_number: null,
        serial_number: null,
        certificate_number: null,
        certificate_type_id: null,
        selected_certificate: null,
        size: null,
        reference_number: null,
        box_and_papers: 'none',
        condition_id: 3,
        selected_condition: null,
        quantity: 1,
        notes: null,
        scale_dwt: null,
        aggregate_dwt: null,
        inventory_status_id: 1,
        additional_packaging: false,
        appraiser_notes: '',
        numismatic_value: null,
        verificationMethods: {
            xrf: true,
            acid: false,
            magnet: true,
            hallmark: true,
            other: false,
        }
    };
}
</script>

<style>
.multiselect__option {
    padding: 8px !important;
    line-height: 10px !important;
    min-height: 20px !important;
    font-size: 0.8em;
}

.multiselect__option--group.multiselect__option--disabled {
    font-weight: bold;
    color: black !important;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.bg-warning-light {
    background-color: #ffe400 !important;
}
.input-warning {
    border: 1px solid #ffc400 !important;
}
.sub-add-accordion a:after {
    font-family: 'Font Awesome 5 Pro';
    content: "\f056";
    float: right;
    color: #888;
}

.sub-add-accordion a.collapsed:after {
    content: "\f055";
}

.delete-photo-icon {
    position: absolute;
    top: 0;
    left: 5px;
    z-index: 1;
}

.extra-option-rows-padding {
}

.no-gutters {
    padding-left: 5px;
    padding-right: 5px;
}

.row-padding {
    padding-left: 10px;
    padding-right: 10px;

}

/* Coin Image Popover */
.flip-container {
    perspective: 1000px;
}

.flip-container:hover .flipper, .flip-container.hover .flipper {
    transform: rotateY(180deg);
}

.flip-container, .front, .back, .coin-image {
    width: 200px;
    height: 200px;
}

.flipper {
    transition: 0.4s;
    transform-style: preserve-3d;
    position: relative;
}

.front, .back {
    backface-visibility: hidden;
    position: absolute;
    top: 0;
    left: 0;
}

.front {
    z-index: 2;
}

.back {
    transform: rotateY(180deg);
    backface-visibility: hidden;
}

.coin-image {
    background-position: left;
    background-size: cover;
}

.back .coin-image {
    background-position: right;
}


.font-xxs {
    font-size: .6rem !important;
}

.label, label {
    font-size: .6rem !important;
}

#appraisals-appraise .label,
#appraisals-appraise label,
#appraisals-appraise .larger-font,
#appraisals-appraise .font-xxs {
    font-size: .7rem !important;
}


.custom-control-label::before {
    left: 10px;
}
.cancel-button {
    background: #f64846;
}
.cancel-button:hover {
    background: #f86c6b;
}
</style>
